<template>
  <div class="container" @mouseover="onInfoTipOver" @mouseout="onInfoTipOut" @focusin="onInfoTipFocusIn" @focusout="onInfoTipFocusOut">
    <h2 class="text-2xl font-semibold mb-2">Engagement Analytics</h2>
    <p class="text-gray-600 mb-4">At-a-glance engagement across your university.</p>
    <div class="mb-6 flex flex-wrap gap-4 items-end">
        <!-- University selector (match OrdersAnalytics style) -->
        <div>
          <label class="block text-sm text-gray-600 mb-1">University</label>
          <template v-if="allowedUniversities.length > 1">
            <select v-model="selectedSlug" class="border rounded px-3 py-2 min-w-[260px]" @change="onUniversityChange">
              <option v-if="isAdmin && allowedUniversities.length > 1" value="_ALL_">All my universities</option>
              <option v-for="u in allowedUniversities" :key="u.slug" :value="u.slug">{{ u.name }} ({{ u.slug }})</option>
            </select>
          </template>
          <template v-else>
            <div class="px-3 py-2 rounded bg-gray-50 border inline-block min-w-[240px]">{{ allowedUniversities[0]?.name || selectedSlug }} ({{ selectedSlug }})</div>
          </template>
        </div>
      </div>

      <div class="mb-6 flex items-center gap-2">
        <button
          @click="setAnalyticsTab('engagement')"
          :class="['px-3 py-2 rounded border text-sm', activeAnalyticsTab === 'engagement' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']"
        >
          Engagement
        </button>
        <button
          v-if="showSingleUniversityTabs"
          @click="setAnalyticsTab('ceremony')"
          :class="['px-3 py-2 rounded border text-sm', activeAnalyticsTab === 'ceremony' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']"
        >
          Ceremony Performance
        </button>
        <button
          v-if="showSingleUniversityTabs"
          @click="setAnalyticsTab('surveys')"
          :class="['px-3 py-2 rounded border text-sm', activeAnalyticsTab === 'surveys' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']"
        >
          Surveys
        </button>
        <button
          v-if="showSingleUniversityTabs"
          @click="setAnalyticsTab('email')"
          :class="['px-3 py-2 rounded border text-sm', activeAnalyticsTab === 'email' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']"
        >
          Email Performance
        </button>
        <button
          v-if="showBenchmarkTab"
          @click="setAnalyticsTab('benchmarks')"
          :class="['px-3 py-2 rounded border text-sm', activeAnalyticsTab === 'benchmarks' ? 'bg-black text-white border-black' : 'bg-white hover:bg-gray-50']"
        >
          Benchmarks
        </button>
      </div>

      <EngagementPanel
        v-if="activeAnalyticsTab === 'engagement'"
        v-bind="engagementPanelProps"
        :tooltips="tooltips"
        @set-live-window="setLiveWindow"
        @apply-preset="applyPreset"
        @update:toggleCustom="toggleCustom = $event"
        @update:customFrom="customFrom = $event"
        @update:customTo="customTo = $event"
        @apply-custom="applyCustom"
        @load-engagement="loadEngagement"
        @export-report="exportReport"
        @update:ceremonyDataScope="ceremonyDataScope = $event"
        @update:ceremonyViewMode="ceremonyViewMode = $event"
        @update:ceremonySortMode="ceremonySortMode = $event"
        @update:ceremonyChartMetric="ceremonyChartMetric = $event"
        @ceremony-data-scope-change="onCeremonyDataScopeChange"
        @toggle-ceremony-expand="toggleCeremonyExpand"
        @load-engagement-insights="loadEngagementInsights"
        @toggle-engagement-deterministic-insights="toggleEngagementDeterministicInsights"
      />

      <CeremonyPerformancePanel
        v-if="activeAnalyticsTab === 'ceremony'"
        v-bind="ceremonyPanelProps"
        :tooltips="tooltips"
        @update:ceremonySeasonKey="ceremonySeasonKey = $event"
        @update:ceremonyInsightsMode="ceremonyInsightsMode = $event"
        @update:ceremonyCompareSeasonKey="ceremonyCompareSeasonKey = $event"
        @update:ceremonyViewMode="ceremonyViewMode = $event"
        @update:ceremonySortMode="ceremonySortMode = $event"
        @update:ceremonyChartMetric="ceremonyChartMetric = $event"
        @season-change="onCeremonySeasonChange"
        @insights-mode-change="onCeremonyInsightsModeChange"
        @compare-season-change="onCeremonyCompareSeasonChange"
        @refresh="refreshCeremonyTab"
        @export-report="exportReport"
        @toggle-ceremony-expand="toggleCeremonyExpand"
        @toggle-deterministic-insights="toggleCeremonyDeterministicInsights"
      />
      <SurveyPanel
        v-if="activeAnalyticsTab === 'surveys'"
        v-bind="surveyPanelProps"
        :tooltips="tooltips"
        v-on="surveyPanelListeners"
      />
      <EmailEngagementPanel
        v-if="activeAnalyticsTab === 'email'"
        :stats="emailStats"
        :loading="emailLoading"
        :error="emailError"
        :presets="presets"
        :active-preset="activePreset"
        :all-time="emailAllTime"
        @apply-preset="applyPreset"
        @load-all="loadAllEmailEngagement"
        @refresh="loadEmailEngagement"
      />

      <div v-if="showBenchmarkTab && activeAnalyticsTab === 'benchmarks'" class="mt-2">
        <AdminBenchmarkOverviewSection
          :loading="benchmarkOverviewLoading"
          :error="benchmarkOverviewError"
          :overview="benchmarkOverview"
          :university-name-by-slug="universityNameBySlug"
          @refresh="loadBenchmarkOverview"
        />
      </div>

      <div
        v-if="floatingTooltip.visible"
        ref="floatingInfoTooltip"
        class="info-tooltip-floating"
        :style="{ left: `${floatingTooltip.left}px`, top: `${floatingTooltip.top}px` }"
      >
        {{ floatingTooltip.text }}
      </div>
  </div>
</template>

<script>
import axios from 'axios'
import Auth from '../../api/auth.js'
import { trackEvent } from '../../Analytics.vue'
import { trackAdminEvent } from '../../admin/activity.js'
import { buildCeremonySeasons } from '../../admin/ceremonySeasons.js'
import EngagementPanel from './analytics/engagement/EngagementPanel.vue'
import AdminBenchmarkOverviewSection from './analytics/engagement/AdminBenchmarkOverviewSection.vue'
import CeremonyPerformancePanel from './analytics/ceremony/CeremonyPerformancePanel.vue'
import SurveyPanel from './analytics/surveys/SurveyPanel.vue'
import EmailEngagementPanel from './analytics/email/EmailEngagementPanel.vue'

export default {
  name: 'AnalyticsDashboard',
  components: {
    EngagementPanel,
    AdminBenchmarkOverviewSection,
    CeremonyPerformancePanel,
    SurveyPanel,
    EmailEngagementPanel,
  },
  data() {
    const payload = Auth.getPayload()
    const universities = Array.isArray(payload?.universities) ? payload.universities : []
    const roles = Array.isArray(payload?.roles) ? payload.roles : []
    return {
      universities,
      roles,
      selectedSlug: universities[0] || '',
      activeAnalyticsTab: 'engagement',
      liveStats: null,
      liveLoading: false,
      liveError: '',
      liveTimer: null,
      allowedUniversities: [],
      start: null,
      end: null,
      activePreset: '7d',
      toggleCustom: false,
      customFrom: '',
      customTo: '',
      presets: [
        { key: '24h', label: 'Last 24h', deltaMs: 24*60*60*1000 },
        { key: '7d', label: 'Last 7 days', deltaMs: 7*24*60*60*1000 },
        { key: '30d', label: 'Last 30 days', deltaMs: 30*24*60*60*1000 },
        { key: '90d', label: 'Last 90 days', deltaMs: 90*24*60*60*1000 },
      ],
      stats: null,
      engagementInsights: null,
      engagementInsightsLoading: false,
      engagementInsightsError: '',
      engagementDeterministicInsightsCollapsed: true,
      benchmarkOverview: null,
      benchmarkOverviewLoading: false,
      benchmarkOverviewError: '',
      errorMsg: '',
      emailStats: null,
      emailLoading: false,
      emailError: '',
      emailAllTime: false,
      loading: false,
      exporting: false,
      exportError: '',
      ceremonyViewMode: 'table',
      ceremonyDataScope: 'season',
      ceremonySeasonKey: 'all',
      ceremonyInsightsMode: 'season_insights',
      ceremonyCompareSeasonKey: '',
      ceremonySelectedCeremonyIds: [],
      ceremonySortMode: 'date_asc',
      ceremonyChartMetric: 'total_views',
      ceremonySeasonRows: [],
      ceremonySeasonLoading: false,
      ceremonySeasonError: '',
      ceremonySeasonScopeKey: '',
      ceremonySeasonScopedStats: null,
      ceremonySeasonScopedInsights: null,
      ceremonySeasonInsightsLoading: false,
      ceremonySeasonInsightsError: '',
      ceremonySeasonFactsLoading: false,
      ceremonySeasonFactsScopeKey: '',
      ceremonySeasonFactsPollRunId: 0,
      ceremonySeasonCompareScopeKey: '',
      ceremonySeasonCompareData: null,
      ceremonySeasonCompareLoading: false,
      ceremonySeasonCompareError: '',
      ceremonySeasonComparePollRunId: 0,
      ceremonySeasonCompareJobStatus: '',
      ceremonyDeterministicInsightsCollapsed: true,
      ceremonyWindowRows: [],
      ceremonyWindowLoading: false,
      ceremonyWindowError: '',
      ceremonyWindowScopeKey: '',
      ceremonyTopClipsLoading: {},
      requestSources: {
        summary: null,
        breakdowns: null,
        engagementInsights: null,
        benchmarkOverview: null,
        ceremonySeason: null,
        ceremonySeasonFacts: null,
        ceremonySeasonInsights: null,
        ceremonySeasonCompare: null,
        ceremonyWindow: null,
        email: null,
      },
      topClipRequestSources: {},
      breakdownsLoading: false,
      surveyStats: null,
      surveyError: '',
      surveyLoading: false,
      surveyCeremonies: [],
      surveySeasonKey: 'all',
      surveySelectedCeremonyIds: [],
      surveySeasonGapDays: 14,
      surveyAnalysisTab: 'overview',
      surveyQuestionSortMode: 'unanswered_desc',
      surveySelectedQuestionKey: '',
      surveyQuestionCeremonyRows: [],
      surveyQuestionCeremonyLoading: false,
      surveyQuestionCeremonyError: '',
      surveyQuestionCeremonyViewMode: 'table',
      surveyQuestionCeremonySortMode: 'date_asc',
      surveyQuestionCeremonyChartMetric: 'answer_rate_pct',
      surveyComparisonStats: null,
      surveyComparisonLoading: false,
      surveyComparisonError: '',
      surveyQuestionCompareLoading: false,
      surveyQuestionCompareError: '',
      surveyComparisonRowsCollapsed: true,
      surveyQuestionComparisonRowsCollapsed: true,
      surveyComparisonChartLimit: 15,
      surveyComparisonChartOrientation: 'horizontal',
      surveyQuestionComparisonChartLimit: 15,
      surveyQuestionComparisonChartOrientation: 'horizontal',
      surveyCompareMode: 'ceremony',
      surveyCompareMetric: 'response_rate_pct',
      surveyCompareSortMode: 'metric_desc',
      surveyCompareQuestionKey: '',
      surveyQuestionCompareMetric: 'answer_rate_pct',
      surveyQuestionCompareSortMode: 'metric_desc',
      surveyOptionChartLimit: 20,
      surveyOptionChartOrientation: 'horizontal',
      surveyCeremonyViewMode: 'table',
      surveyCeremonySortMode: 'date_asc',
      surveyCeremonyChartMetric: 'response_rate_pct',
      surveyInsights: null,
      surveyInsightsLoading: false,
      surveyInsightsNarrativeLoading: false,
      surveyInsightsError: '',
      surveyInsightsSegmentBy: 'ceremony',
      surveyInsightsPollRunId: 0,
      surveyInsightsGeneratedAt: '',
      surveyInsightsLoadedFromCache: false,
      surveyInsightCardExpanded: {},
      surveyDetailedInsightExpanded: {},
      surveyCeremonyDropdownOpen: false,
      surveyCeremonySelectionDraft: [],
      surveyInsightsCaveatsCollapsed: true,
      surveyExportMode: 'full_dataset',
      surveyExportFieldPreset: 'full_response',
      surveyExportQuestionKey: '',
      surveyExportAnswerValues: [],
      surveyExportPermissionQuestionKey: '',
      surveyExportPermissionAnswerValues: [],
      surveyExportContactableOnly: true,
      surveyExportLoading: false,
      surveyExportPreviewLoading: false,
      surveyExportError: '',
      surveyExportPreview: null,
      surveyReportPack: 'executive',
      surveyReportLoading: false,
      surveyReportError: '',
      expandedCeremonies: {},
      clipNameBySubmission: {},
      clipNameInflight: {},
      floatingTooltip: {
        visible: false,
        text: '',
        left: 0,
        top: 0,
      },
      activeInfoTipEl: null,
      // Live windows (in seconds)
      liveWindows: [30, 60, 300, 900],
      activeLiveWindow: 30,
      tooltips: {
        unique_views: 'Distinct clips (submissions) viewed at least once within the selected time range.',
        total_views: 'Total number of clip view events in the selected time range (includes repeats by the same user/session).',
        total_downloads: 'Total number of DOWNLOAD_CLIP events recorded in the selected time range.',
        unique_clips_shared: 'Number of distinct clips that were shared at least once during the selected time range.',
        total_shares: 'Total number of SHARE events recorded in the selected time range.',
        sessions: 'Distinct sessions counted by session ID within the selected time range.',
        page_views: 'Total PAGE events in the selected time range (all pages).',
        share_distribution: 'Breakdown of unique clips shared by platform; percentages sum to 100% across platforms.',
        timezones: 'Top timezones by distinct sessions; one count per session’s reported timezone.',
        top_sources: 'Top referrer sources for clip views using the normalized ref_host from events.',
        by_ceremony: 'Ceremony-level breakdown with top clips by total views in the selected range.',
        graduate_amplification_index: 'Average audience reached per graduate clip.',
        additional_audience_per_graduate: 'Average number of extra viewers each graduate generated beyond themselves.',
        observed_share_amplification: 'Average number of additional clip views created by each share.',
        download_rate: 'Percentage of graduates who downloaded their clip.',
        projected_total_impressions: 'The projected range of off-platform views outside of MyGraduationClip',
        survey_response: 'Survey analytics based on current registration answers, grouped by ceremony and question.',
        survey_eligible: 'Registrations where this question is applicable (displayed by survey rules/conditions). This is per-question and may be lower than total registrations.',
      },
    }
  },
  computed: {
    isAllScope() {
      return this.selectedSlug === '_ALL_'
    },
    isAdmin() {
      return (this.roles || []).includes('admin')
    },
    canLoadAllScopeEngagement() {
      return this.isAllScope && this.isAdmin
    },
    showBenchmarkTab() {
      return this.isAdmin && this.isAllScope
    },
    showSingleUniversityTabs() {
      return !this.isAllScope
    },
    engagementScopeSlugs() {
      if (this.isAllScope) {
        return (this.allowedUniversities || [])
          .map(u => String(u?.slug || '').trim())
          .filter(Boolean)
      }
      return this.selectedSlug ? [String(this.selectedSlug)] : []
    },
    universityNameBySlug() {
      const map = {}
      for (const row of (this.allowedUniversities || [])) {
        const slug = String(row?.slug || '').trim()
        if (!slug) continue
        map[slug] = String(row?.name || slug)
      }
      return map
    },
    selectedUniversity() {
      return (this.allowedUniversities || []).find(u => u.slug === this.selectedSlug) || null
    },
    selectedUniversityName() {
      return this.selectedUniversity ? this.selectedUniversity.name : this.selectedSlug
    },
    engagementPanelProps() {
      return {
        liveWindows: this.liveWindows,
        activeLiveWindow: this.activeLiveWindow,
        liveLoading: this.liveLoading,
        liveError: this.liveError,
        liveStats: this.liveStats,
        isAllScope: this.isAllScope,
        allowAllScopeHistorical: this.canLoadAllScopeEngagement,
        presets: this.presets,
        activePreset: this.activePreset,
        toggleCustom: this.toggleCustom,
        customFrom: this.customFrom,
        customTo: this.customTo,
        exporting: this.exporting,
        exportError: this.exportError,
        loading: this.loading,
        breakdownsLoading: this.breakdownsLoading,
        engagementInsightsLoading: this.engagementInsightsLoading,
        isCeremonyBreakdownLoading: this.isCeremonyBreakdownLoading,
        engagementPrimaryLoadingText: this.engagementPrimaryLoadingText,
        errorMsg: this.errorMsg,
        stats: this.stats,
        engagementHeadlineSummary: this.engagementHeadlineSummary,
        engagementSelectedScopeLabel: this.engagementSelectedScopeLabel,
        ceremonyDataScope: this.ceremonyDataScope,
        ceremonyViewMode: this.ceremonyViewMode,
        ceremonySortMode: this.ceremonySortMode,
        ceremonyChartMetric: this.ceremonyChartMetric,
        ceremonyBreakdownError: this.ceremonyBreakdownError,
        ceremonyRowsSorted: this.ceremonyRowsSorted,
        chartMetricLabel: this.chartMetricLabel,
        engagementInsightsError: this.engagementInsightsError,
        engagementKeyInsights: this.engagementKeyInsights,
        engagementInsightCards: this.engagementInsightCards,
        engagementDeterministicInsightsCollapsed: this.engagementDeterministicInsightsCollapsed,
        formatWindowFn: this.formatWindow,
        ceremonyTitleFn: this.ceremonyTitle,
        isCeremonyTopClipsLoadingFn: this.isCeremonyTopClipsLoading,
        clipNameFn: this.clipName,
        isCeremonyExpandedFn: this.isCeremonyExpanded,
        metricValueFn: this.metricValue,
        ceremonyMetricWidthFn: this.ceremonyMetricWidth,
        geoWidthFn: this.geoWidth,
        isAdmin: this.isAdmin,
      }
    },
    ceremonyPanelProps() {
      return {
        isAllScope: this.isAllScope,
        surveySeasons: this.surveySeasons,
        ceremonySeasonKey: this.ceremonySeasonKey,
        ceremonyInsightsMode: this.ceremonyInsightsMode,
        ceremonyCompareSeasonKey: this.ceremonyCompareSeasonKey,
        ceremonySeasonRows: this.ceremonySeasonRows,
        ceremonySeasonLoading: this.ceremonySeasonLoading,
        ceremonySeasonError: this.ceremonySeasonError,
        ceremonySeasonScopedStats: this.ceremonySeasonScopedStats,
        ceremonySeasonScopedInsights: this.ceremonySeasonScopedInsights,
        ceremonySeasonInsightsLoading: this.ceremonySeasonInsightsLoading,
        ceremonySeasonInsightsError: this.ceremonySeasonInsightsError,
        ceremonySeasonFactsLoading: this.ceremonySeasonFactsLoading,
        ceremonySeasonCompareData: this.ceremonySeasonCompareData,
        ceremonySeasonCompareLoading: this.ceremonySeasonCompareLoading,
        ceremonySeasonCompareError: this.ceremonySeasonCompareError,
        ceremonySeasonCompareJobStatus: this.ceremonySeasonCompareJobStatus,
        ceremonyDeterministicInsightsCollapsed: this.ceremonyDeterministicInsightsCollapsed,
        exporting: this.exporting,
        exportError: this.exportError,
        ceremonyViewMode: this.ceremonyViewMode,
        ceremonySortMode: this.ceremonySortMode,
        ceremonyChartMetric: this.ceremonyChartMetric,
        expandedCeremonies: this.expandedCeremonies,
        ceremonyTopClipsLoading: this.ceremonyTopClipsLoading,
        clipNameBySubmission: this.clipNameBySubmission,
        selectedSlug: this.selectedSlug,
        isAdmin: this.isAdmin,
      }
    },
    surveyPanelProps() {
      return {
        state: {
          surveySeasonKey: this.surveySeasonKey,
          surveyCeremonyViewMode: this.surveyCeremonyViewMode,
          surveyCeremonySortMode: this.surveyCeremonySortMode,
          surveyCeremonyChartMetric: this.surveyCeremonyChartMetric,
          surveyQuestionSortMode: this.surveyQuestionSortMode,
          surveyQuestionCeremonyViewMode: this.surveyQuestionCeremonyViewMode,
          surveyQuestionCeremonySortMode: this.surveyQuestionCeremonySortMode,
          surveyQuestionCeremonyChartMetric: this.surveyQuestionCeremonyChartMetric,
          surveyCompareMode: this.surveyCompareMode,
          surveyCompareMetric: this.surveyCompareMetric,
          surveyCompareSortMode: this.surveyCompareSortMode,
          surveyCompareQuestionKey: this.surveyCompareQuestionKey,
          surveyQuestionCompareMetric: this.surveyQuestionCompareMetric,
          surveyQuestionCompareSortMode: this.surveyQuestionCompareSortMode,
          surveyComparisonRowsCollapsed: this.surveyComparisonRowsCollapsed,
          surveyQuestionComparisonRowsCollapsed: this.surveyQuestionComparisonRowsCollapsed,
          surveyComparisonChartLimit: this.surveyComparisonChartLimit,
          surveyComparisonChartOrientation: this.surveyComparisonChartOrientation,
          surveyQuestionComparisonChartLimit: this.surveyQuestionComparisonChartLimit,
          surveyQuestionComparisonChartOrientation: this.surveyQuestionComparisonChartOrientation,
          surveyOptionChartLimit: this.surveyOptionChartLimit,
          surveyOptionChartOrientation: this.surveyOptionChartOrientation,
          surveyInsightsSegmentBy: this.surveyInsightsSegmentBy,
          isAllScope: this.isAllScope,
          surveySeasons: this.surveySeasons,
          surveyCeremonyDropdownOpen: this.surveyCeremonyDropdownOpen,
          surveyCeremonySelectionLabel: this.surveyCeremonySelectionLabel,
          surveyCeremonyOptionsForControl: this.surveyCeremonyOptionsForControl,
          surveyCeremonySelectionDraft: this.surveyCeremonySelectionDraft,
          surveyLoading: this.surveyLoading,
          surveyError: this.surveyError,
          surveyStats: this.surveyStats,
          surveyAnalysisTab: this.surveyAnalysisTab,
          surveyQuestionsSorted: this.surveyQuestionsSorted,
          selectedSurveyQuestion: this.selectedSurveyQuestion,
          selectedSurveyQuestionOptions: this.selectedSurveyQuestionOptions,
          surveyQuestionCeremonyLoading: this.surveyQuestionCeremonyLoading,
          surveyQuestionCeremonyError: this.surveyQuestionCeremonyError,
          surveyQuestionCeremonyRowsSorted: this.surveyQuestionCeremonyRowsSorted,
          surveyQuestionCeremonyChartMetricLabel: this.surveyQuestionCeremonyChartMetricLabel,
          surveyCeremonyRowsSorted: this.surveyCeremonyRowsSorted,
          surveyChartMetricLabel: this.surveyChartMetricLabel,
          surveyComparisonLoading: this.surveyComparisonLoading,
          surveyComparisonError: this.surveyComparisonError,
          surveyComparisonBaseline: this.surveyComparisonBaseline,
          surveyCompareMetricLabel: this.surveyCompareMetricLabel,
          surveyComparisonRowsSorted: this.surveyComparisonRowsSorted,
          surveyComparisonMetricSuffix: this.surveyComparisonMetricSuffix,
          selectedSurveyCompareQuestion: this.selectedSurveyCompareQuestion,
          surveyComparisonQuestions: this.surveyComparisonQuestions,
          surveyQuestionCompareLoading: this.surveyQuestionCompareLoading,
          surveyQuestionCompareError: this.surveyQuestionCompareError,
          surveyQuestionComparisonRowsSorted: this.surveyQuestionComparisonRowsSorted,
          surveyQuestionCompareMetricLabel: this.surveyQuestionCompareMetricLabel,
          isSurveyCompareOptionQuestion: this.isSurveyCompareOptionQuestion,
          surveyQuestionOptionLegend: this.surveyQuestionOptionLegend,
          surveyQuestionAvailableOptions: this.surveyQuestionAvailableOptions,
          surveyInsightsLoading: this.surveyInsightsLoading,
          surveyInsightsNarrativeLoading: this.surveyInsightsNarrativeLoading,
          surveyInsightsError: this.surveyInsightsError,
          surveyInsights: this.surveyInsights,
          surveyInsightsGeneratedAt: this.surveyInsightsGeneratedAt,
          surveyInsightsLoadedFromCache: this.surveyInsightsLoadedFromCache,
          surveySelectedCeremonyIds: this.surveySelectedCeremonyIds,
          surveyInsightsNarrativeHeadline: this.surveyInsightsNarrativeHeadline,
          surveyInsightsNarrativeInsights: this.surveyInsightsNarrativeInsights,
          surveyInsightsNarrativeNextSteps: this.surveyInsightsNarrativeNextSteps,
          surveyTopFindings: this.surveyTopFindings,
          surveyVisualPatterns: this.surveyVisualPatterns,
          surveyKeyInsights: this.surveyKeyInsights,
          surveyDetailedInsights: this.surveyDetailedInsights,
          surveyInsightsRecommendedActions: this.surveyInsightsRecommendedActions,
          surveyInsightsNarrativeCaveats: this.surveyInsightsNarrativeCaveats,
          surveyInsightsCaveatsCollapsed: this.surveyInsightsCaveatsCollapsed,
          surveyExportMode: this.surveyExportMode,
          surveyExportFieldPreset: this.surveyExportFieldPreset,
          surveyExportQuestionKey: this.surveyExportQuestionKey,
          surveyExportAnswerValues: this.surveyExportAnswerValues,
          surveyExportPermissionQuestionKey: this.surveyExportPermissionQuestionKey,
          surveyExportPermissionAnswerValues: this.surveyExportPermissionAnswerValues,
          surveyExportPermissionQuestionOptions: this.surveyExportPermissionQuestionOptions,
          surveyExportContactableOnly: this.surveyExportContactableOnly,
          surveyExportLoading: this.surveyExportLoading,
          surveyExportPreviewLoading: this.surveyExportPreviewLoading,
          surveyExportError: this.surveyExportError,
          surveyExportPreview: this.surveyExportPreview,
          surveyExportQuestions: this.surveyExportQuestions,
          surveyExportQuestionOptions: this.surveyExportQuestionOptions,
          canExportSurvey: this.canExportSurvey,
          surveyReportPack: this.surveyReportPack,
          surveyReportLoading: this.surveyReportLoading,
          surveyReportError: this.surveyReportError,
        },
        actions: {
          onSurveySeasonChange: this.onSurveySeasonChange,
          toggleSurveyCeremonyDropdown: this.toggleSurveyCeremonyDropdown,
          selectAllSurveyCeremonyDraft: this.selectAllSurveyCeremonyDraft,
          clearSurveyCeremonyDraft: this.clearSurveyCeremonyDraft,
          onSurveyCeremonyDraftChange: this.onSurveyCeremonyDraftChange,
          closeSurveyCeremonyDropdown: this.closeSurveyCeremonyDropdown,
          applySurveyCeremonySelection: this.applySurveyCeremonySelection,
          formatSurveyCeremonyOption: this.formatSurveyCeremonyOption,
          loadSurvey: this.loadSurvey,
          setSurveyAnalysisTab: this.setSurveyAnalysisTab,
          selectSurveyQuestion: this.selectSurveyQuestion,
          isBinaryQuestion: this.isBinaryQuestion,
          surveyOptionWidth: this.surveyOptionWidth,
          ceremonyTitle: this.ceremonyTitle,
          surveyQuestionMetricValue: this.surveyQuestionMetricValue,
          surveyQuestionMetricWidth: this.surveyQuestionMetricWidth,
          surveyMetricValue: this.surveyMetricValue,
          surveyMetricWidth: this.surveyMetricWidth,
          onSurveyCompareModeChange: this.onSurveyCompareModeChange,
          surveyComparisonMetricDisplay: this.surveyComparisonMetricDisplay,
          surveyComparisonRowChartLabel: this.surveyComparisonRowChartLabel,
          surveyComparisonMetricValue: this.surveyComparisonMetricValue,
          surveyDeltaClass: this.surveyDeltaClass,
          surveyComparisonDeltaValue: this.surveyComparisonDeltaValue,
          formatSurveyDelta: this.formatSurveyDelta,
          onSurveyCompareQuestionChange: this.onSurveyCompareQuestionChange,
          surveyQuestionComparisonMetricValue: this.surveyQuestionComparisonMetricValue,
          surveyQuestionOptionSeriesLabel: this.surveyQuestionOptionSeriesLabel,
          surveyQuestionOptionSeriesValue: this.surveyQuestionOptionSeriesValue,
          surveyQuestionOptionRowKey: this.surveyQuestionOptionRowKey,
          surveyQuestionOptionColorByValue: this.surveyQuestionOptionColorByValue,
          onSurveyInsightsSegmentByChange: this.onSurveyInsightsSegmentByChange,
          explainSurveyInsights: this.explainSurveyInsights,
          regenerateSurveyInsights: this.regenerateSurveyInsights,
          formatInsightsGeneratedAt: this.formatInsightsGeneratedAt,
          insightCardId: this.insightCardId,
          insightCategoryLabel: this.insightCategoryLabel,
          insightTone: this.insightTone,
          insightConfidenceLabel: this.insightConfidenceLabel,
          formatInsightText: this.formatInsightText,
          compactInsightSummary: this.compactInsightSummary,
          insightPrimaryMetric: this.insightPrimaryMetric,
          insightMetricBarPercent: this.insightMetricBarPercent,
          insightPrimaryMetricLabel: this.insightPrimaryMetricLabel,
          insightPrimaryMetricValue: this.insightPrimaryMetricValue,
          applySurveyInsightLink: this.applySurveyInsightLink,
          isInsightCardExpanded: this.isInsightCardExpanded,
          toggleInsightCard: this.toggleInsightCard,
          isInsightExpanded: this.isInsightExpanded,
          toggleSurveyInsightCard: this.toggleSurveyInsightCard,
          isDetailedInsightExpanded: this.isDetailedInsightExpanded,
          toggleDetailedInsight: this.toggleDetailedInsight,
          toggleSurveyInsightsCaveats: this.toggleSurveyInsightsCaveats,
          surveyExportModeLabel: this.surveyExportModeLabel,
          surveyExportNeedsQuestion: this.surveyExportNeedsQuestion,
          surveyExportNeedsAnswers: this.surveyExportNeedsAnswers,
          surveyExportUsesPermissionFilter: this.surveyExportUsesPermissionFilter,
          surveyExportUsesContactableFilter: this.surveyExportUsesContactableFilter,
          canPreviewSurveyExport: this.canPreviewSurveyExport,
          canDownloadSurveyExport: this.canDownloadSurveyExport,
          canRunSurveyExportTask: this.canRunSurveyExportTask,
          onSurveyExportModeChange: this.onSurveyExportModeChange,
          onSurveyExportFieldPresetChange: this.onSurveyExportFieldPresetChange,
          onSurveyExportQuestionChange: this.onSurveyExportQuestionChange,
          onSurveyExportAnswerChange: this.onSurveyExportAnswerChange,
          selectAllSurveyExportAnswers: this.selectAllSurveyExportAnswers,
          clearSurveyExportAnswers: this.clearSurveyExportAnswers,
          onSurveyExportPermissionQuestionChange: this.onSurveyExportPermissionQuestionChange,
          onSurveyExportPermissionAnswerChange: this.onSurveyExportPermissionAnswerChange,
          selectAllSurveyExportPermissionAnswers: this.selectAllSurveyExportPermissionAnswers,
          clearSurveyExportPermissionAnswers: this.clearSurveyExportPermissionAnswers,
          configureSurveyExportTask: this.configureSurveyExportTask,
          previewSurveyExportTask: this.previewSurveyExportTask,
          downloadSurveyExportTask: this.downloadSurveyExportTask,
          previewSurveyExport: this.previewSurveyExport,
          downloadSurveyExport: this.downloadSurveyExport,
          downloadSurveyReport: this.downloadSurveyReport,
        },
      }
    },
    surveyPanelListeners() {
      return {
        'update:surveySeasonKey': (value) => { this.surveySeasonKey = value },
        'update:surveyCeremonyViewMode': (value) => { this.surveyCeremonyViewMode = value },
        'update:surveyCeremonySortMode': (value) => { this.surveyCeremonySortMode = value },
        'update:surveyCeremonyChartMetric': (value) => { this.surveyCeremonyChartMetric = value },
        'update:surveyQuestionSortMode': (value) => { this.surveyQuestionSortMode = value },
        'update:surveyQuestionCeremonyViewMode': (value) => { this.surveyQuestionCeremonyViewMode = value },
        'update:surveyQuestionCeremonySortMode': (value) => { this.surveyQuestionCeremonySortMode = value },
        'update:surveyQuestionCeremonyChartMetric': (value) => { this.surveyQuestionCeremonyChartMetric = value },
        'update:surveyCompareMode': (value) => { this.surveyCompareMode = value },
        'update:surveyCompareMetric': (value) => { this.surveyCompareMetric = value },
        'update:surveyCompareSortMode': (value) => { this.surveyCompareSortMode = value },
        'update:surveyCompareQuestionKey': (value) => { this.surveyCompareQuestionKey = value },
        'update:surveyQuestionCompareMetric': (value) => { this.surveyQuestionCompareMetric = value },
        'update:surveyQuestionCompareSortMode': (value) => { this.surveyQuestionCompareSortMode = value },
        'update:surveyComparisonRowsCollapsed': (value) => { this.surveyComparisonRowsCollapsed = value },
        'update:surveyQuestionComparisonRowsCollapsed': (value) => { this.surveyQuestionComparisonRowsCollapsed = value },
        'update:surveyComparisonChartLimit': (value) => { this.surveyComparisonChartLimit = value },
        'update:surveyComparisonChartOrientation': (value) => { this.surveyComparisonChartOrientation = value },
        'update:surveyQuestionComparisonChartLimit': (value) => { this.surveyQuestionComparisonChartLimit = value },
        'update:surveyQuestionComparisonChartOrientation': (value) => { this.surveyQuestionComparisonChartOrientation = value },
        'update:surveyOptionChartLimit': (value) => { this.surveyOptionChartLimit = value },
        'update:surveyOptionChartOrientation': (value) => { this.surveyOptionChartOrientation = value },
        'update:surveyInsightsSegmentBy': (value) => { this.surveyInsightsSegmentBy = value },
        'update:surveyExportMode': (value) => { this.surveyExportMode = value },
        'update:surveyExportFieldPreset': (value) => { this.surveyExportFieldPreset = value },
        'update:surveyExportQuestionKey': (value) => { this.surveyExportQuestionKey = value },
        'update:surveyExportPermissionQuestionKey': (value) => { this.surveyExportPermissionQuestionKey = value },
        'update:surveyExportContactableOnly': (value) => { this.surveyExportContactableOnly = value },
        'update:surveyReportPack': (value) => { this.surveyReportPack = value },
      }
    },
    engagementPrimaryLoadingText() {
      if (this.loading) return 'Loading engagement summary metrics...'
      if (this.breakdownsLoading && this.engagementInsightsLoading) return 'Loading detailed breakdowns and generating insights...'
      if (this.breakdownsLoading) return 'Loading detailed breakdowns...'
      if (this.engagementInsightsLoading) return 'Generating curated engagement insights...'
      if (this.isCeremonyBreakdownLoading) {
        return this.ceremonyDataScope === 'window'
          ? 'Loading time-range ceremony breakdown...'
          : 'Loading season ceremony breakdown...'
      }
      return 'Loading engagement analytics...'
    },
    ceremonySeasonCompareJobStatusLabel() {
      const status = String(this.ceremonySeasonCompareJobStatus || '').toLowerCase()
      if (status === 'queued') return 'Queued'
      if (status === 'running') return 'Running'
      if (status === 'completed') return 'Completed'
      if (status === 'failed') return 'Failed'
      if (status === 'submitting') return 'Submitting'
      return 'Starting'
    },
    ceremonySeasonCompareLoadingText() {
      const status = String(this.ceremonySeasonCompareJobStatus || '').toLowerCase()
      if (status === 'queued') return 'Comparison job is queued and waiting for a worker.'
      if (status === 'running') return 'Computing season deltas and generating narrative insights.'
      if (status === 'submitting') return 'Submitting season comparison request...'
      return 'Preparing season comparison insights...'
    },
    engagementInsightCards() {
      const rows = this.engagementInsights?.questions
      return Array.isArray(rows) ? rows : []
    },
    engagementKeyInsights() {
      const rows = this.engagementInsights?.key_insights
      return Array.isArray(rows) ? rows : []
    },
    engagementHeadlineSummary() {
      const row = this.engagementInsights?.headline_summary
      return row && typeof row === 'object' ? row : null
    },
    ceremonyScopedStats() {
      const raw = this.ceremonySeasonScopedStats && typeof this.ceremonySeasonScopedStats === 'object'
        ? this.ceremonySeasonScopedStats
        : {}
      return {
        unique_views: Number(raw.unique_views || 0),
        total_views: Number(raw.total_views || 0),
        total_shares: Number(raw.total_shares || 0),
        total_downloads: Number(raw.total_downloads || 0),
        graduates: Number(raw.graduates || 0),
        graduate_amplification_index: (raw.graduate_amplification_index ?? null),
        graduate_amplification_index_label: String(raw.graduate_amplification_index_label || ''),
        projected_total_impressions_range: String(raw.projected_total_impressions_range || ''),
        projected_total_impressions_lower_multiplier: Number(raw.projected_total_impressions_lower_multiplier || 0),
        projected_total_impressions_upper_multiplier: Number(raw.projected_total_impressions_upper_multiplier || 0),
        additional_audience_per_graduate: (raw.additional_audience_per_graduate ?? null),
        additional_audience_per_graduate_label: String(raw.additional_audience_per_graduate_label || ''),
        observed_share_amplification: (raw.observed_share_amplification ?? null),
        observed_share_amplification_label: String(raw.observed_share_amplification_label || ''),
        observed_share_amplification_reliability: String(raw.observed_share_amplification_reliability || ''),
        observed_share_amplification_reliability_label: String(raw.observed_share_amplification_reliability_label || ''),
        download_rate: (raw.download_rate ?? null),
        download_rate_label: String(raw.download_rate_label || ''),
        metric_tooltips: raw.metric_tooltips && typeof raw.metric_tooltips === 'object' ? raw.metric_tooltips : {},
        benchmark_yearly: raw.benchmark_yearly && typeof raw.benchmark_yearly === 'object' ? raw.benchmark_yearly : null,
        sessions: Number(raw.sessions || 0),
        page_views: Number(raw.page_views || 0),
        unique_clips_shared: Number(raw.unique_clips_shared || 0),
        share_platforms: Array.isArray(raw.share_platforms) ? raw.share_platforms : [],
        timezones_reached: Number(raw.timezones_reached || 0),
        top_timezones: Array.isArray(raw.top_timezones) ? raw.top_timezones : [],
        top_sources: Array.isArray(raw.top_sources) ? raw.top_sources : [],
      }
    },
    ceremonyScopedInsights() {
      const row = this.ceremonySeasonScopedInsights
      return row && typeof row === 'object' ? row : null
    },
    ceremonyScopedHeadlineSummary() {
      const row = this.ceremonyScopedInsights?.headline_summary
      return row && typeof row === 'object' ? row : null
    },
    ceremonyScopedKeyInsights() {
      const rows = this.ceremonyScopedInsights?.key_insights
      return Array.isArray(rows) ? rows : []
    },
    ceremonyScopedInsightCards() {
      const rows = this.ceremonyScopedInsights?.questions
      return Array.isArray(rows) ? rows : []
    },
    ceremonySeasonScopeLabel() {
      if (this.ceremonySeasonKey === 'all') return 'All ceremonies'
      const season = (this.surveySeasons || []).find(s => s.key === this.ceremonySeasonKey)
      return String(season?.label || 'Selected season')
    },
    ceremonyCompareSeasonOptions() {
      if (this.ceremonySeasonKey === 'all') return []
      const seasons = Array.isArray(this.surveySeasons) ? this.surveySeasons : []
      return seasons.filter(s => String(s?.key || '') && s.key !== this.ceremonySeasonKey)
    },
    ceremonyCompareSeasonLabel() {
      const found = (this.ceremonyCompareSeasonOptions || []).find(s => s.key === this.ceremonyCompareSeasonKey)
      return String(found?.label || '')
    },
    ceremonySeasonCompareHeadline() {
      const row = this.ceremonySeasonCompareData?.headline_summary
      return row && typeof row === 'object' ? row : null
    },
    ceremonySeasonCompareKeyInsights() {
      const rows = this.ceremonySeasonCompareData?.key_insights
      return Array.isArray(rows) ? rows : []
    },
    ceremonySeasonCompareMetricRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.metric_deltas)
        ? this.ceremonySeasonCompareData.metric_deltas
        : []
      return [...rows]
        .sort((a, b) => Number(b?.abs_strength || 0) - Number(a?.abs_strength || 0))
        .slice(0, 10)
    },
    ceremonySeasonComparePlatformRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.platform_deltas)
        ? this.ceremonySeasonCompareData.platform_deltas
        : []
      return [...rows]
        .sort((a, b) => Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0)))
        .slice(0, 8)
    },
    ceremonySeasonCompareSourceRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.source_deltas)
        ? this.ceremonySeasonCompareData.source_deltas
        : []
      return [...rows]
        .sort((a, b) => Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0)))
        .slice(0, 8)
    },
    ceremonySeasonCompareTimezoneRows() {
      const rows = Array.isArray(this.ceremonySeasonCompareData?.timezone_deltas)
        ? this.ceremonySeasonCompareData.timezone_deltas
        : []
      return [...rows]
        .sort((a, b) => {
          const bCount = Math.abs(Number(b?.delta_count || 0))
          const aCount = Math.abs(Number(a?.delta_count || 0))
          if (bCount !== aCount) return bCount - aCount
          return Math.abs(Number(b?.delta_pp || 0)) - Math.abs(Number(a?.delta_pp || 0))
        })
        .slice(0, 8)
    },
    ceremonySeasonCompareTimezoneMaxCount() {
      const rows = this.ceremonySeasonCompareTimezoneRows
      const maxCount = rows.reduce((max, row) => {
        const cur = Number(row?.current_count || 0)
        const base = Number(row?.baseline_count || 0)
        return Math.max(max, cur, base)
      }, 0)
      return maxCount > 0 ? maxCount : 1
    },
    ceremonySeasonCompareCurrentTimezonesReached() {
      return Number(this.ceremonySeasonCompareData?.current_scope?.summary_snapshot?.timezones_reached || 0)
    },
    ceremonySeasonCompareBaselineTimezonesReached() {
      return Number(this.ceremonySeasonCompareData?.baseline_scope?.summary_snapshot?.timezones_reached || 0)
    },
    ceremonySeasonCompareTimezonesReachedDelta() {
      return this.ceremonySeasonCompareCurrentTimezonesReached - this.ceremonySeasonCompareBaselineTimezonesReached
    },
    ceremonyComparisonKpiCards() {
      const current = this.ceremonySeasonCompareData?.current_scope?.summary_snapshot || {}
      const baseline = this.ceremonySeasonCompareData?.baseline_scope?.summary_snapshot || {}
      const metricDefs = [
        { id: 'unique_views', title: 'Unique clip views', key: 'total_unique_views', kind: 'count', tooltip_key: 'unique_views' },
        { id: 'total_views', title: 'Total clip views', key: 'total_views', kind: 'count', tooltip_key: 'total_views' },
        { id: 'graduate_amplification_index', title: 'Graduate Amplification Index', key: 'graduate_amplification_index', kind: 'amplification_index', tooltip_key: 'graduate_amplification_index' },
        { id: 'additional_audience_per_graduate', title: 'Additional Audience per Graduate', key: 'additional_audience_per_graduate', kind: 'rate', tooltip_key: 'additional_audience_per_graduate' },
        { id: 'observed_share_amplification', title: 'Views Generated per Share', key: 'observed_share_amplification', kind: 'rate', tooltip_key: 'observed_share_amplification' },
        { id: 'total_downloads', title: 'Total downloads', key: 'total_downloads', kind: 'count', tooltip_key: 'total_downloads' },
        { id: 'unique_clips_shared', title: 'Unique clips shared', key: 'unique_clips_shared', kind: 'count', tooltip_key: 'unique_clips_shared' },
        { id: 'total_shares', title: 'Total clip shares', key: 'total_shares', kind: 'count', tooltip_key: 'total_shares' },
        { id: 'shares_per_100_views', title: 'Shares per 100 views', key: 'shares_per_100_views', kind: 'rate', tooltip_key: 'share_distribution' },
        { id: 'downloads_per_100_views', title: 'Downloads per 100 views', key: 'downloads_per_100_views', kind: 'rate', tooltip_key: 'total_downloads' },
      ]
      return metricDefs.map((def) => {
        const currentValue = Number(current?.[def.key] || 0)
        const baselineValue = Number(baseline?.[def.key] || 0)
        const deltaPct = baselineValue > 0 ? ((currentValue - baselineValue) / baselineValue) * 100 : null
        return {
          ...def,
          current_value: currentValue,
          baseline_value: baselineValue,
          delta_pct: Number.isFinite(deltaPct) ? deltaPct : null,
        }
      })
    },
    engagementSelectedScopeLabel() {
      const preset = (this.presets || []).find(p => p?.key === this.activePreset)
      if (preset?.label) return String(preset.label)
      const startTs = Number(this.start)
      const endTs = Number(this.end)
      const fmt = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
      if (Number.isFinite(startTs) && Number.isFinite(endTs) && startTs > 0 && endTs > 0) {
        return `${fmt.format(new Date(startTs))} to ${fmt.format(new Date(endTs))}`
      }
      if (Number.isFinite(startTs) && startTs > 0) {
        return `From ${fmt.format(new Date(startTs))}`
      }
      if (Number.isFinite(endTs) && endTs > 0) {
        return `Up to ${fmt.format(new Date(endTs))}`
      }
      return 'Current selected range'
    },
    ceremonyRowsBase() {
      const useWindowRows = this.activeAnalyticsTab !== 'ceremony' && this.ceremonyDataScope === 'window'
      const rows = useWindowRows ? this.ceremonyWindowRows : this.ceremonySeasonRows
      return Array.isArray(rows)
        ? rows.filter(row => !this.isUnmappedCeremonyRow(row))
        : []
    },
    isCeremonyBreakdownLoading() {
      if (this.activeAnalyticsTab === 'ceremony') return this.ceremonySeasonLoading
      return this.ceremonyDataScope === 'window' ? this.ceremonyWindowLoading : this.ceremonySeasonLoading
    },
    ceremonyBreakdownError() {
      if (this.activeAnalyticsTab === 'ceremony') return this.ceremonySeasonError
      return this.ceremonyDataScope === 'window' ? this.ceremonyWindowError : this.ceremonySeasonError
    },
    ceremonyRowsSorted() {
      const rows = [...this.ceremonyRowsBase]
      const dt = (row) => {
        if (!row || !row.ceremony_datetime) return 0
        const t = new Date(row.ceremony_datetime).getTime()
        return Number.isFinite(t) ? t : 0
      }
      if (this.ceremonySortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
      }
      if (this.ceremonySortMode === 'views_desc') {
        return rows.sort((a, b) => (b.total_views || 0) - (a.total_views || 0) || dt(a) - dt(b))
      }
      if (this.ceremonySortMode === 'shares_desc') {
        return rows.sort((a, b) => (b.total_shares || 0) - (a.total_shares || 0) || dt(a) - dt(b))
      }
      if (this.ceremonySortMode === 'downloads_desc') {
        return rows.sort((a, b) => (b.total_downloads || 0) - (a.total_downloads || 0) || dt(a) - dt(b))
      }
      return rows.sort((a, b) => dt(a) - dt(b) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
    },
    chartMetricLabel() {
      if (this.ceremonyChartMetric === 'unique_views') return 'Unique views'
      if (this.ceremonyChartMetric === 'total_shares') return 'Shares'
      if (this.ceremonyChartMetric === 'total_downloads') return 'Downloads'
      return 'Total views'
    },
    surveySeasons() {
      return buildCeremonySeasons(this.surveyCeremonies, {
        gapDays: this.surveySeasonGapDays,
      })
    },
    surveyCeremonyOptionsForControl() {
      const ceremonies = Array.isArray(this.surveyCeremonies) ? this.surveyCeremonies : []
      if (this.surveySeasonKey === 'all') return ceremonies
      const allowed = new Set(this.ceremonyIdsForSeasonKey(this.surveySeasonKey))
      return ceremonies.filter(c => allowed.has(String(c.id)))
    },
    surveyCeremonySelectionLabel() {
      const selected = Array.isArray(this.surveySelectedCeremonyIds)
        ? this.surveySelectedCeremonyIds.map(v => String(v))
        : []
      if (!selected.length) return 'No ceremonies selected'
      const options = this.surveyCeremonyOptionsForControl
      if (selected.length === options.length && options.length > 0) return 'All ceremonies selected'
      if (selected.length === 1) {
        const found = options.find(c => String(c.id) === selected[0]) || (this.surveyCeremonies || []).find(c => String(c.id) === selected[0])
        return found ? this.formatSurveyCeremonyOption(found) : '1 ceremony selected'
      }
      return `${selected.length} ceremonies selected`
    },
    surveyQuestionsSorted() {
      const rows = Array.isArray(this.surveyStats?.questions) ? [...this.surveyStats.questions] : []
      if (this.surveyQuestionSortMode === 'answer_rate_desc') {
        return rows.sort((a, b) => (b.answer_rate_pct || 0) - (a.answer_rate_pct || 0) || (b.eligible_count || 0) - (a.eligible_count || 0))
      }
      if (this.surveyQuestionSortMode === 'answer_rate_asc') {
        return rows.sort((a, b) => (a.answer_rate_pct || 0) - (b.answer_rate_pct || 0) || (b.eligible_count || 0) - (a.eligible_count || 0))
      }
      if (this.surveyQuestionSortMode === 'eligible_desc') {
        return rows.sort((a, b) => (b.eligible_count || 0) - (a.eligible_count || 0) || (a.answer_rate_pct || 0) - (b.answer_rate_pct || 0))
      }
      return rows.sort((a, b) => (b.unanswered_count || 0) - (a.unanswered_count || 0) || (a.answer_rate_pct || 0) - (b.answer_rate_pct || 0))
    },
    selectedSurveyQuestion() {
      const rows = this.surveyQuestionsSorted
      if (!rows.length) return null
      const selected = rows.find(q => q.question_key === this.surveySelectedQuestionKey)
      return selected || rows[0]
    },
    selectedSurveyQuestionOptions() {
      const q = this.selectedSurveyQuestion
      return Array.isArray(q?.options) ? q.options : []
    },
    surveyExportQuestions() {
      return Array.isArray(this.surveyStats?.questions) ? this.surveyStats.questions : []
    },
    canExportSurvey() {
      const roleSet = new Set(Array.isArray(this.roles) ? this.roles : [])
      return roleSet.has('admin') || roleSet.has('analytics-exporter') || roleSet.has('survey-exporter')
    },
    selectedSurveyExportQuestion() {
      const rows = this.surveyExportQuestions
      if (!rows.length) return null
      const selected = rows.find(q => String(q?.question_key || '') === String(this.surveyExportQuestionKey || ''))
      return selected || rows[0]
    },
    surveyExportQuestionOptions() {
      const q = this.selectedSurveyExportQuestion
      return Array.isArray(q?.options) ? q.options : []
    },
    selectedSurveyExportPermissionQuestion() {
      const rows = this.surveyExportQuestions
      if (!rows.length || !this.surveyExportPermissionQuestionKey) return null
      return rows.find(q => String(q?.question_key || '') === String(this.surveyExportPermissionQuestionKey || '')) || null
    },
    surveyExportPermissionQuestionOptions() {
      const q = this.selectedSurveyExportPermissionQuestion
      return Array.isArray(q?.options) ? q.options : []
    },
    surveyQuestionCeremonyRowsSorted() {
      const rows = Array.isArray(this.surveyQuestionCeremonyRows) ? [...this.surveyQuestionCeremonyRows] : []
      const dt = (row) => {
        if (!row || !row.ceremony_datetime) return 0
        const t = new Date(row.ceremony_datetime).getTime()
        return Number.isFinite(t) ? t : 0
      }
      if (this.surveyQuestionCeremonySortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
      }
      if (this.surveyQuestionCeremonySortMode === 'answer_rate_desc') {
        return rows.sort((a, b) => (b.answer_rate_pct || 0) - (a.answer_rate_pct || 0) || dt(a) - dt(b))
      }
      if (this.surveyQuestionCeremonySortMode === 'answered_desc') {
        return rows.sort((a, b) => (b.answered_count || 0) - (a.answered_count || 0) || dt(a) - dt(b))
      }
      if (this.surveyQuestionCeremonySortMode === 'eligible_desc') {
        return rows.sort((a, b) => (b.eligible_count || 0) - (a.eligible_count || 0) || dt(a) - dt(b))
      }
      return rows.sort((a, b) => dt(a) - dt(b) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
    },
    surveyQuestionCeremonyChartMetricLabel() {
      if (this.surveyQuestionCeremonyChartMetric === 'answered_count') return 'Answered'
      if (this.surveyQuestionCeremonyChartMetric === 'eligible_count') return 'Eligible'
      return 'Answer rate %'
    },
    surveyCeremonyRowsSorted() {
      const rows = Array.isArray(this.surveyStats?.by_ceremony) ? [...this.surveyStats.by_ceremony] : []
      const dt = (row) => {
        if (!row || !row.ceremony_datetime) return 0
        const t = new Date(row.ceremony_datetime).getTime()
        return Number.isFinite(t) ? t : 0
      }
      if (this.surveyCeremonySortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
      }
      if (this.surveyCeremonySortMode === 'response_rate_desc') {
        return rows.sort((a, b) => (b.response_rate_pct || 0) - (a.response_rate_pct || 0) || dt(a) - dt(b))
      }
      if (this.surveyCeremonySortMode === 'responders_desc') {
        return rows.sort((a, b) => (b.responders || 0) - (a.responders || 0) || dt(a) - dt(b))
      }
      if (this.surveyCeremonySortMode === 'registrations_desc') {
        return rows.sort((a, b) => (b.total_registrations || 0) - (a.total_registrations || 0) || dt(a) - dt(b))
      }
      return rows.sort((a, b) => dt(a) - dt(b) || String(a.ceremony_id || '').localeCompare(String(b.ceremony_id || '')))
    },
    surveyChartMetricLabel() {
      if (this.surveyCeremonyChartMetric === 'responders') return 'Responders'
      if (this.surveyCeremonyChartMetric === 'total_registrations') return 'Registrations'
      return 'Response rate %'
    },
    surveyComparisonSourceRows() {
      return Array.isArray(this.surveyComparisonStats?.by_ceremony) ? this.surveyComparisonStats.by_ceremony : []
    },
    surveyComparisonBaseline() {
      const rows = this.surveyComparisonSourceRows
      const totals = rows.reduce((acc, row) => {
        acc.total_registrations += Number(row?.total_registrations || 0)
        acc.responders += Number(row?.responders || 0)
        acc.total_eligible_questions += Number(row?.total_eligible_questions || 0)
        acc.total_answered_questions += Number(row?.total_answered_questions || 0)
        return acc
      }, {
        total_registrations: 0,
        responders: 0,
        total_eligible_questions: 0,
        total_answered_questions: 0,
      })
      const rate = totals.total_registrations > 0 ? (100 * totals.responders / totals.total_registrations) : 0
      const completion = totals.total_eligible_questions > 0
        ? (100 * totals.total_answered_questions / totals.total_eligible_questions)
        : 0
      return {
        total_registrations: totals.total_registrations,
        responders: totals.responders,
        total_eligible_questions: totals.total_eligible_questions,
        total_answered_questions: totals.total_answered_questions,
        response_rate_pct: Number(rate.toFixed(2)),
        question_completion_rate_pct: Number(completion.toFixed(2)),
      }
    },
    surveyComparisonCeremonyRows() {
      const baseline = Number(this.surveyComparisonBaseline.response_rate_pct || 0)
      const completionBaseline = Number(this.surveyComparisonBaseline.question_completion_rate_pct || 0)
      return this.surveyComparisonSourceRows.map((row) => {
        const responseRate = Number(row?.response_rate_pct || 0)
        const eligibleQuestions = Number(row?.total_eligible_questions || 0)
        const answeredQuestions = Number(row?.total_answered_questions || 0)
        const completionRate = Number(
          row?.question_completion_rate_pct !== undefined && row?.question_completion_rate_pct !== null
            ? row.question_completion_rate_pct
            : (eligibleQuestions > 0 ? (100 * answeredQuestions / eligibleQuestions) : 0),
        )
        return {
          ...row,
          compare_key: String(row?.ceremony_id || ''),
          question_completion_rate_pct: Number(completionRate.toFixed(2)),
          delta_response_rate_pct: Number((responseRate - baseline).toFixed(2)),
          delta_question_completion_rate_pct: Number((completionRate - completionBaseline).toFixed(2)),
        }
      })
    },
    surveyComparisonCohortRows() {
      const seasonByCeremonyId = {}
      for (const season of (this.surveySeasons || [])) {
        for (const ceremonyId of (season.ceremonyIds || [])) {
          seasonByCeremonyId[String(ceremonyId)] = season
        }
      }

      const grouped = {}
      for (const row of this.surveyComparisonSourceRows) {
        const ceremonyId = String(row?.ceremony_id || '')
        const season = seasonByCeremonyId[ceremonyId]
        const key = season?.key || `unmapped-${ceremonyId || 'unknown'}`
        if (!grouped[key]) {
          grouped[key] = {
            compare_key: key,
            label: season?.label || `Unmapped (${ceremonyId || 'unknown'})`,
            startTs: season?.startTs || null,
            endTs: season?.endTs || null,
            ceremony_count: 0,
            total_registrations: 0,
            responders: 0,
            total_eligible_questions: 0,
            total_answered_questions: 0,
          }
        }
        grouped[key].ceremony_count += 1
        grouped[key].total_registrations += Number(row?.total_registrations || 0)
        grouped[key].responders += Number(row?.responders || 0)
        grouped[key].total_eligible_questions += Number(row?.total_eligible_questions || 0)
        grouped[key].total_answered_questions += Number(row?.total_answered_questions || 0)
      }

      const baseline = Number(this.surveyComparisonBaseline.response_rate_pct || 0)
      const completionBaseline = Number(this.surveyComparisonBaseline.question_completion_rate_pct || 0)
      return Object.values(grouped).map((row) => {
        const rate = row.total_registrations > 0 ? (100 * row.responders / row.total_registrations) : 0
        const completion = row.total_eligible_questions > 0
          ? (100 * row.total_answered_questions / row.total_eligible_questions)
          : 0
        return {
          ...row,
          response_rate_pct: Number(rate.toFixed(2)),
          delta_response_rate_pct: Number((rate - baseline).toFixed(2)),
          question_completion_rate_pct: Number(completion.toFixed(2)),
          delta_question_completion_rate_pct: Number((completion - completionBaseline).toFixed(2)),
        }
      })
    },
    surveyComparisonRowsSorted() {
      const rows = this.surveyCompareMode === 'cohort'
        ? [...this.surveyComparisonCohortRows]
        : [...this.surveyComparisonCeremonyRows]
      const metricValue = (row) => this.surveyComparisonMetricValue(row)
      const dt = (row) => {
        const ts = this.surveyCompareMode === 'cohort'
          ? Number(row?.endTs || row?.startTs || 0)
          : new Date(row?.ceremony_datetime || 0).getTime()
        return Number.isFinite(ts) ? ts : 0
      }
      if (this.surveyCompareSortMode === 'metric_asc') {
        return rows.sort((a, b) => metricValue(a) - metricValue(b) || dt(a) - dt(b))
      }
      if (this.surveyCompareSortMode === 'delta_desc') {
        return rows.sort((a, b) => (this.surveyComparisonDeltaValue(b) - this.surveyComparisonDeltaValue(a)) || dt(a) - dt(b))
      }
      if (this.surveyCompareSortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.compare_key || '').localeCompare(String(b.compare_key || '')))
      }
      if (this.surveyCompareSortMode === 'date_asc') {
        return rows.sort((a, b) => dt(a) - dt(b) || String(a.compare_key || '').localeCompare(String(b.compare_key || '')))
      }
      return rows.sort((a, b) => metricValue(b) - metricValue(a) || dt(a) - dt(b))
    },
    surveyCompareMetricLabel() {
      if (this.surveyCompareMetric === 'question_completion_rate_pct') return 'Question completion %'
      if (this.surveyCompareMetric === 'responders') return 'Responders'
      if (this.surveyCompareMetric === 'total_registrations') return 'Registrations'
      return 'Response rate %'
    },
    surveyComparisonMetricSuffix() {
      if (this.surveyCompareMetric === 'response_rate_pct' || this.surveyCompareMetric === 'question_completion_rate_pct') {
        return '%'
      }
      return ''
    },
    surveyComparisonQuestions() {
      const rows = Array.isArray(this.surveyComparisonStats?.questions)
        ? this.surveyComparisonStats.questions
        : (Array.isArray(this.surveyStats?.questions) ? this.surveyStats.questions : [])
      return rows
    },
    selectedSurveyCompareQuestion() {
      const rows = this.surveyComparisonQuestions
      if (!rows.length) return null
      const selected = rows.find(q => String(q?.question_key || '') === String(this.surveyCompareQuestionKey || ''))
      return selected || rows[0]
    },
    isSurveyCompareOptionQuestion() {
      const qType = String(this.selectedSurveyCompareQuestion?.question_type || '').toLowerCase()
      return qType === 'radio' || qType === 'select' || qType === 'multi-select'
    },
    surveyQuestionComparisonSourceRows() {
      const selectedKey = this.selectedSurveyCompareQuestion?.question_key
      if (!selectedKey) return []
      const rows = Array.isArray(this.surveyComparisonStats?.question_ceremony) ? this.surveyComparisonStats.question_ceremony : []
      return rows.filter(r => String(r?.question_key || '') === String(selectedKey))
    },
    surveyQuestionComparisonBaseline() {
      const totals = this.surveyQuestionComparisonSourceRows.reduce((acc, row) => {
        acc.eligible_count += Number(row?.eligible_count || 0)
        acc.answered_count += Number(row?.answered_count || 0)
        return acc
      }, { eligible_count: 0, answered_count: 0 })
      const rate = totals.eligible_count > 0 ? (100 * totals.answered_count / totals.eligible_count) : 0
      return {
        eligible_count: totals.eligible_count,
        answered_count: totals.answered_count,
        answer_rate_pct: Number(rate.toFixed(2)),
      }
    },
    surveyQuestionComparisonCeremonyRows() {
      const baseline = Number(this.surveyQuestionComparisonBaseline.answer_rate_pct || 0)
      return this.surveyQuestionComparisonSourceRows.map((row) => {
        const answerRate = Number(row?.answer_rate_pct || 0)
        return {
          ...row,
          compare_key: String(row?.ceremony_id || ''),
          delta_answer_rate_pct: Number((answerRate - baseline).toFixed(2)),
        }
      })
    },
    surveyQuestionComparisonCohortRows() {
      const seasonByCeremonyId = {}
      for (const season of (this.surveySeasons || [])) {
        for (const ceremonyId of (season.ceremonyIds || [])) {
          seasonByCeremonyId[String(ceremonyId)] = season
        }
      }
      const grouped = {}
      for (const row of this.surveyQuestionComparisonSourceRows) {
        const ceremonyId = String(row?.ceremony_id || '')
        const season = seasonByCeremonyId[ceremonyId]
        const key = season?.key || `unmapped-${ceremonyId || 'unknown'}`
        if (!grouped[key]) {
          grouped[key] = {
            compare_key: key,
            label: season?.label || `Unmapped (${ceremonyId || 'unknown'})`,
            startTs: season?.startTs || null,
            endTs: season?.endTs || null,
            ceremony_count: 0,
            eligible_count: 0,
            answered_count: 0,
            options_by_value: {},
          }
        }
        grouped[key].ceremony_count += 1
        grouped[key].eligible_count += Number(row?.eligible_count || 0)
        grouped[key].answered_count += Number(row?.answered_count || 0)
        const options = Array.isArray(row?.options) ? row.options : []
        for (const opt of options) {
          const value = String(opt?.value || '')
          if (!value) continue
          if (!grouped[key].options_by_value[value]) {
            grouped[key].options_by_value[value] = {
              value,
              label: opt?.label || value,
              count: 0,
            }
          }
          grouped[key].options_by_value[value].count += Number(opt?.count || 0)
        }
      }
      const baseline = Number(this.surveyQuestionComparisonBaseline.answer_rate_pct || 0)
      return Object.values(grouped).map((row) => {
        const rate = row.eligible_count > 0 ? (100 * row.answered_count / row.eligible_count) : 0
        const options = Object.values(row.options_by_value || {})
          .sort((a, b) => (Number(b.count || 0) - Number(a.count || 0)) || String(a.value || '').localeCompare(String(b.value || '')))
          .map((opt) => {
            const count = Number(opt.count || 0)
            return {
              value: opt.value,
              label: opt.label || opt.value,
              count,
              percent_of_answered: row.answered_count > 0
                ? Number(((100 * count) / row.answered_count).toFixed(2))
                : 0,
            }
          })
        return {
          ...row,
          answer_rate_pct: Number(rate.toFixed(2)),
          delta_answer_rate_pct: Number((rate - baseline).toFixed(2)),
          options,
        }
      })
    },
    surveyQuestionComparisonRowsSorted() {
      const rows = this.surveyCompareMode === 'cohort'
        ? [...this.surveyQuestionComparisonCohortRows]
        : [...this.surveyQuestionComparisonCeremonyRows]
      const metricValue = (row) => this.surveyQuestionComparisonMetricValue(row)
      const dt = (row) => {
        const ts = this.surveyCompareMode === 'cohort'
          ? Number(row?.endTs || row?.startTs || 0)
          : new Date(row?.ceremony_datetime || 0).getTime()
        return Number.isFinite(ts) ? ts : 0
      }
      if (this.surveyQuestionCompareSortMode === 'metric_asc') {
        return rows.sort((a, b) => metricValue(a) - metricValue(b) || dt(a) - dt(b))
      }
      if (this.surveyQuestionCompareSortMode === 'delta_desc') {
        return rows.sort((a, b) => (Number(b?.delta_answer_rate_pct || 0) - Number(a?.delta_answer_rate_pct || 0)) || dt(a) - dt(b))
      }
      if (this.surveyQuestionCompareSortMode === 'date_desc') {
        return rows.sort((a, b) => dt(b) - dt(a) || String(a.compare_key || '').localeCompare(String(b.compare_key || '')))
      }
      if (this.surveyQuestionCompareSortMode === 'date_asc') {
        return rows.sort((a, b) => dt(a) - dt(b) || String(a.compare_key || '').localeCompare(String(b.compare_key || '')))
      }
      return rows.sort((a, b) => metricValue(b) - metricValue(a) || dt(a) - dt(b))
    },
    surveyQuestionCompareMetricLabel() {
      if (this.surveyQuestionCompareMetric === 'answered_count') return 'Answered'
      if (this.surveyQuestionCompareMetric === 'eligible_count') return 'Eligible'
      return 'Answer rate %'
    },
    surveyQuestionAvailableOptions() {
      const map = {}
      const rows = this.surveyQuestionComparisonRowsSorted
      for (const row of rows) {
        const options = Array.isArray(row?.options) ? row.options : []
        for (const opt of options) {
          const value = String(opt?.value || '')
          if (!value || map[value]) continue
          map[value] = {
            value,
            label: opt?.label || value,
          }
        }
      }
      return Object.values(map).sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')))
    },
    surveyQuestionOptionLegend() {
      const palette = ['#111827', '#0f766e', '#2563eb', '#b45309', '#be123c', '#6d28d9', '#0e7490', '#4d7c0f']
      const options = this.surveyQuestionAvailableOptions
      const normalize = (opt) => String(opt?.label || opt?.value || '').trim().toLowerCase()
      const yesLike = new Set(['yes', 'y', 'true'])
      const noLike = new Set(['no', 'n', 'false'])
      const yesOpt = options.find(opt => yesLike.has(normalize(opt)))
      const noOpt = options.find(opt => noLike.has(normalize(opt)))
      if (yesOpt && noOpt && options.length === 2) {
        return [
          { ...yesOpt, color: '#16a34a' }, // green
          { ...noOpt, color: '#dc2626' },  // red
        ]
      }
      return options.map((opt, idx) => ({
        ...opt,
        color: palette[idx % palette.length],
      }))
    },
    surveyInsightsDeepLinks() {
      const links = this.surveyInsights?.deep_links
      return links && typeof links === 'object' ? links : {}
    },
    surveyInsightsNarrativeV2() {
      const narrative = this.surveyInsights?.narrative_v2
      return narrative && typeof narrative === 'object' ? narrative : null
    },
    surveyInsightsHasV2() {
      return Boolean(this.surveyInsightsNarrativeV2)
    },
    surveyInsightsNarrativeHeadline() {
      const headline = this.surveyInsightsNarrativeV2?.headline || this.surveyInsights?.narrative?.headline
      if (!headline || typeof headline !== 'object') return { title: '', summary: '', scope_note: '' }
      return {
        title: String(headline.title || ''),
        summary: String(headline.summary || ''),
        scope_note: String(headline.scope_note || ''),
      }
    },
    surveyInsightsNarrativeInsights() {
      if (this.surveyInsightsHasV2) {
        const v2 = this.surveyInsightsNarrativeV2 || {}
        const top = Array.isArray(v2.top_findings) ? v2.top_findings : []
        const insights = Array.isArray(v2.insights) ? v2.insights : []
        const composites = Array.isArray(v2.composites) ? v2.composites : []
        return [...top, ...insights, ...composites]
      }
      const rows = this.surveyInsights?.narrative?.insights
      return Array.isArray(rows) ? rows : []
    },
    surveyInsightsNarrativeNextSteps() {
      if (this.surveyInsightsHasV2) {
        const rows = this.surveyInsightsNarrativeV2?.next_steps
        return Array.isArray(rows) ? rows : []
      }
      const rows = this.surveyInsights?.narrative?.next_steps
      return Array.isArray(rows) ? rows : []
    },
    surveyInsightsNarrativeCaveats() {
      if (this.surveyInsightsHasV2) {
        const caveats = this.surveyInsightsNarrativeV2?.caveats
        return Array.isArray(caveats) ? caveats : []
      }
      const caveats = this.surveyInsights?.narrative?.caveats
      return Array.isArray(caveats) ? caveats : []
    },
    surveyTopFindings() {
      if (this.surveyInsightsHasV2) {
        const rows = this.surveyInsightsNarrativeV2?.top_findings
        return Array.isArray(rows) ? rows : []
      }
      return this.surveyInsightsNarrativeInsights.slice(0, 3)
    },
    surveyKeyInsights() {
      if (this.surveyInsightsHasV2) {
        const v2 = this.surveyInsightsNarrativeV2 || {}
        const keys = Array.isArray(v2.key_insights) ? v2.key_insights : []
        if (keys.length) return keys
        const rows = Array.isArray(v2.insights) ? v2.insights : []
        return rows.slice(0, 4)
      }
      const insights = this.surveyInsightsNarrativeInsights
      const topCount = Math.min(3, insights.length)
      const remaining = Math.max(0, insights.length - topCount)
      const keyCount = Math.min(4, remaining)
      return insights.slice(topCount, topCount + keyCount)
    },
    surveyDetailedInsights() {
      if (this.surveyInsightsHasV2) {
        const v2 = this.surveyInsightsNarrativeV2 || {}
        const detailed = Array.isArray(v2.detailed_insights) ? v2.detailed_insights : []
        const composites = Array.isArray(v2.composites) ? v2.composites : []
        if (detailed.length) return [...detailed, ...composites]
        const rows = Array.isArray(v2.insights) ? v2.insights : []
        return [...rows.slice(4), ...composites]
      }
      const insights = this.surveyInsightsNarrativeInsights
      const topCount = Math.min(3, insights.length)
      const remaining = Math.max(0, insights.length - topCount)
      const keyCount = Math.min(4, remaining)
      return insights.slice(topCount + keyCount)
    },
    surveyVisualPatterns() {
      const cards = this.surveyInsightsNarrativeInsights.slice(0, 8)
      if (!cards.length) return []
      const metrics = cards.map((card) => this.insightPrimaryMetric(card))
      const maxCount = Math.max(
        ...metrics
          .filter(metric => metric.kind === 'count')
          .map(metric => Number(metric.number || 0)),
        0,
      )
      return cards.map((card, idx) => {
        const metric = metrics[idx] || {}
        const tone = this.insightTone(card)
        return {
          id: this.insightCardId(card, idx, 'pattern'),
          title: String(card?.title || 'Insight'),
          metricValue: String(metric.value || 'Signal'),
          metricLabel: String(metric.label || 'Metric'),
          barPercent: this.insightMetricBarPercent(metric, card, maxCount),
          barColor: tone.barColor,
        }
      })
    },
    surveyInsightsRecommendedActions() {
      const steps = this.surveyInsightsNarrativeNextSteps
      if (steps.length) return steps
      const fallback = this.surveyInsightsNarrativeInsights
        .filter(card => String(card?.category || '').toLowerCase() === 'next_steps')
      return fallback.slice(0, 4)
    },
  },
  methods: {
    startRequestSource(slot) {
      const existing = this.requestSources?.[slot]
      if (existing && typeof existing.cancel === 'function') {
        existing.cancel('Superseded by a newer request')
      }
      const source = axios.CancelToken.source()
      this.$set(this.requestSources, slot, source)
      return source
    },
    finishRequestSource(slot, source) {
      if (this.requestSources?.[slot] === source) {
        this.$set(this.requestSources, slot, null)
      }
    },
    engagementThemeText(theme) {
      const key = String(theme || '').trim().toLowerCase()
      if (key === 'reach') return 'Reach'
      if (key === 'action') return 'Action'
      if (key === 'source') return 'Source'
      if (key === 'geo') return 'Geo'
      if (key === 'concentration') return 'Concentration'
      return key || 'Insight'
    },
    cancelStatsRequests() {
      for (const slot of ['summary', 'breakdowns', 'engagementInsights', 'benchmarkOverview', 'ceremonySeason', 'ceremonySeasonFacts', 'ceremonySeasonInsights', 'ceremonySeasonCompare', 'ceremonyWindow', 'email']) {
        const source = this.requestSources?.[slot]
        if (source && typeof source.cancel === 'function') {
          source.cancel('Cancelled')
        }
        this.$set(this.requestSources, slot, null)
      }
    },
    startTopClipRequestSource(ceremonyId) {
      const key = String(ceremonyId || '')
      const existing = this.topClipRequestSources?.[key]
      if (existing && typeof existing.cancel === 'function') {
        existing.cancel('Superseded by a newer request')
      }
      const source = axios.CancelToken.source()
      this.$set(this.topClipRequestSources, key, source)
      return source
    },
    finishTopClipRequestSource(ceremonyId, source) {
      const key = String(ceremonyId || '')
      if (this.topClipRequestSources?.[key] === source) {
        this.$delete(this.topClipRequestSources, key)
      }
    },
    cancelTopClipRequests() {
      const keys = Object.keys(this.topClipRequestSources || {})
      for (const key of keys) {
        const source = this.topClipRequestSources[key]
        if (source && typeof source.cancel === 'function') {
          source.cancel('Cancelled')
        }
        this.$delete(this.topClipRequestSources, key)
      }
    },
    isRequestCancelled(err) {
      return Boolean(err && axios.isCancel && axios.isCancel(err))
    },
    formatWindow(w) {
      if (w < 60) return `${w}s`
      if (w % 60 === 0 && w < 3600) return `${w/60}m`
      return `${(w/3600).toFixed(1)}h`
    },
    setLiveWindow(w) {
      this.activeLiveWindow = w
      if (this.activeAnalyticsTab === 'engagement') {
        this.loadLive()
      }
    },
    setAnalyticsTab(tab) {
      if (tab !== 'engagement' && tab !== 'ceremony' && tab !== 'surveys' && tab !== 'email' && tab !== 'benchmarks') return
      if (tab === 'benchmarks' && !this.showBenchmarkTab) return
      if ((tab === 'ceremony' || tab === 'surveys' || tab === 'email') && !this.showSingleUniversityTabs) return
      this.activeAnalyticsTab = tab
      if (tab === 'engagement') {
        this.startLiveAutoRefresh()
      } else {
        this.stopLiveAutoRefresh()
      }
      if (tab === 'engagement') {
        if (this.isAllScope && !this.canLoadAllScopeEngagement) return
        if (!this.stats && !this.loading) {
          this.loadEngagement()
          return
        }
        return
      }
      if (tab === 'ceremony') {
        if (this.isAllScope) return
        this.ceremonyDataScope = 'season'
        this.loadSeasonCeremonyBreakdown({ loadInsights: true })
        return
      }
      if (tab === 'email') {
        if (this.isAllScope) return
        this.loadEmailEngagement()
        return
      }
      if (tab === 'benchmarks') {
        if (!this.isAdmin) return
        this.loadBenchmarkOverview()
        return
      }
      if (this.isAllScope) return
      if (!this.surveyCeremonies.length) {
        this.refreshSurveyCeremonies()
          .then(() => this.loadSurvey())
          .catch(() => {
            this.surveyError = 'Failed to load ceremonies for survey scoping.'
          })
        return
      }
      if (!this.surveyStats && !this.surveyLoading) this.loadSurvey()
    },
    setSurveyAnalysisTab(tab) {
      if (!['overview', 'questions', 'ceremony', 'compare', 'insights'].includes(tab)) return
      this.surveyAnalysisTab = tab
      if (tab === 'insights') {
        if (this.surveyCompareMode === 'cohort') {
          this.surveyInsightsSegmentBy = 'cohort'
        }
        if (!this.surveyInsights && !this.surveyInsightsLoading && !this.surveyInsightsNarrativeLoading) {
          this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: false })
        }
        try {
          trackEvent(this.$route?.path || '/admin/analytics', {
            label: 'ANALYTICS_SURVEY_INSIGHTS_TAB_VIEW',
            segment_by: this.surveyInsightsSegmentBy,
          })
        } catch (_) { void 0 }
      }
    },
    surveyInsightsScopePayload() {
      const payload = {
        universitySlug: this.selectedSlug,
        ceremonyIds: (this.surveySelectedCeremonyIds || []).map(v => String(v)),
        segmentBy: this.surveyInsightsSegmentBy === 'cohort' ? 'cohort' : 'ceremony',
      }
      return payload
    },
    async loadSurveyInsights({ includeNarrative = true, silent = false, forceRefresh = false } = {}) {
      if (this.selectedSlug === '_ALL_') return
      const pollRunId = Date.now()
      this.surveyInsightsPollRunId = pollRunId
      if (silent) {
        this.surveyInsightsNarrativeLoading = true
      } else {
        this.surveyInsightsLoading = true
      }
      try {
        this.surveyInsightsError = ''
        this.surveyInsightsLoadedFromCache = false
        this.surveyInsightsGeneratedAt = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const createUrl = base + '/stats/survey/insights/jobs'
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const payload = {
          ...this.surveyInsightsScopePayload(),
          includeNarrative: Boolean(includeNarrative),
          includeNarrativeV2: true,
          forceRefresh: Boolean(forceRefresh),
        }
        if (!silent) {
          try {
            trackEvent(this.$route?.path || '/admin/analytics', {
              label: 'ANALYTICS_SURVEY_INSIGHTS_EXPLAIN_RESULTS',
              include_narrative: Boolean(includeNarrative),
              segment_by: payload.segmentBy,
            })
          } catch (_) { void 0 }
        }
        const createRes = await axios.post(createUrl, payload, { headers })
        const createData = createRes.data || {}
        this.surveyInsightsLoadedFromCache = Boolean(createData?.reused)
        this.surveyInsightsGeneratedAt = String(createData?.generated_at || '')
        let nextData = null
        if (createData && createData.job_id) {
          const jobResult = await this.pollSurveyInsightsJob({
            base,
            headers,
            jobId: String(createData.job_id),
            pollRunId,
          })
          if (pollRunId !== this.surveyInsightsPollRunId) return
          if (!jobResult || !jobResult.result) {
            const failedMessage = String(jobResult?.message || '').trim()
            this.surveyInsightsError = failedMessage || 'AI insights are currently unavailable. Please try again.'
            return
          }
          this.surveyInsightsGeneratedAt = String(jobResult?.generated_at || jobResult?.completed_at || this.surveyInsightsGeneratedAt || '')
          nextData = jobResult.result
        } else {
          // Fallback to direct payload if endpoint returns immediate result.
          nextData = createData
        }
        this.surveyInsights = nextData
        this.surveyInsightsCaveatsCollapsed = true
        this.surveyInsightCardExpanded = {}
        this.surveyDetailedInsightExpanded = {}
        if (nextData && !nextData.narrative_v2 && !nextData.narrative) {
          this.surveyInsightsError = 'AI insights are currently unavailable. Please try again.'
        }
      } catch (e) {
        if (!silent) this.surveyInsights = null
        if (!silent) this.surveyDetailedInsightExpanded = {}
        if (!silent) this.surveyInsightCardExpanded = {}
        if (!silent) this.surveyInsightsGeneratedAt = ''
        if (!silent) this.surveyInsightsLoadedFromCache = false
        try {
          this.surveyInsightsError =
            (e && e.response && e.response.data && e.response.data.message) ||
            (e && e.message) ||
            'Unexpected error'
        } catch (_) {
          this.surveyInsightsError = 'Unexpected error'
        }
      } finally {
        if (silent) {
          if (pollRunId === this.surveyInsightsPollRunId) this.surveyInsightsNarrativeLoading = false
        } else {
          if (pollRunId === this.surveyInsightsPollRunId) this.surveyInsightsLoading = false
        }
      }
    },
    async pollSurveyInsightsJob({ base, headers, jobId, pollRunId }) {
      if (!jobId) return null
      const statusUrl = `${base}/stats/survey/insights/jobs/${encodeURIComponent(jobId)}`
      const maxAttempts = 120
      let waitMs = 2000
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (pollRunId !== this.surveyInsightsPollRunId) return null
        const res = await axios.get(statusUrl, { headers })
        const data = res.data || {}
        const status = String(data.status || '').toLowerCase()
        if (status === 'completed') return data
        if (status === 'failed') return data
        if (attempt >= 14) waitMs = 5000
        await new Promise(resolve => setTimeout(resolve, waitMs))
      }
      return { status: 'failed', message: 'Insights request timed out. Please try again.' }
    },
    async explainSurveyInsights() {
      await this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: false })
    },
    async regenerateSurveyInsights() {
      await this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: true })
    },
    async applySurveyInsightLink(insight) {
      const insightId = String(insight?.id || insight?.finding_id || '')
      const narrativeV2Link = insightId ? this.surveyInsights?.narrative_v2_links?.[insightId] : null
      const narrativeLink = narrativeV2Link || (insightId ? this.surveyInsights?.narrative_links?.[insightId] : null)
      const directLink = insight?.link || null
      const deepLinkKey = String(insight?.drilldown?.deep_link_key || '').trim()
      const keyedLink = deepLinkKey ? this.surveyInsightsDeepLinks?.[deepLinkKey] : null
      const link = narrativeLink || directLink || keyedLink || null
      if (!link) return
      try {
        trackEvent(this.$route?.path || '/admin/analytics', {
          label: 'ANALYTICS_SURVEY_INSIGHT_CLICK',
          finding_id: insightId,
          analysis_tab: String(link.analysis_tab || ''),
          question_key: String(link.question_key || ''),
          deep_link_key: deepLinkKey,
        })
      } catch (_) { void 0 }
      const targetTab = String(link.analysis_tab || '').trim()
      if (!targetTab) return
      if (targetTab === 'overview') {
        this.setSurveyAnalysisTab('overview')
        return
      }
      if (targetTab === 'questions') {
        this.setSurveyAnalysisTab('questions')
        if (link.question_key) {
          this.selectSurveyQuestion(String(link.question_key))
        }
        return
      }
      if (targetTab === 'ceremony') {
        this.setSurveyAnalysisTab('ceremony')
        this.surveyCeremonyViewMode = 'chart'
        return
      }
      if (targetTab === 'compare') {
        const targetMode = String(link.compare_mode || this.surveyCompareMode || 'ceremony')
        if (targetMode === 'ceremony' || targetMode === 'cohort') {
          this.surveyCompareMode = targetMode
        }
        if (link.compare_metric) this.surveyCompareMetric = String(link.compare_metric)
        if (link.question_metric) this.surveyQuestionCompareMetric = String(link.question_metric)
        if (link.question_key) this.surveyCompareQuestionKey = String(link.question_key)
        this.setSurveyAnalysisTab('compare')
        const comparisonCeremonyIds = this.comparisonCeremonyIdsForMode(this.surveyCompareMode)
        await this.loadSurveyComparison({
          ceremonyIds: comparisonCeremonyIds,
          questionKey: this.surveyCompareQuestionKey,
          loadingScope: link.question_key ? 'question' : 'global',
        })
        return
      }
      this.setSurveyAnalysisTab('overview')
    },
    onCeremonyDataScopeChange() {
      if (this.ceremonyDataScope === 'window') {
        this.loadWindowCeremonyBreakdown()
      } else {
        this.ensureCeremonyCompareSeasonKey()
        this.loadSeasonCeremonyBreakdown()
      }
      this.expandedCeremonies = {}
      if (this.ceremonyInsightsMode !== 'season_comparison') {
        this.prefetchCeremonyNames()
      }
    },
    onCeremonySeasonChange() {
      this.ceremonySelectedCeremonyIds = this.ceremonyIdsForSeasonKey(this.ceremonySeasonKey)
      this.ensureCeremonyCompareSeasonKey()
      this.ceremonyDataScope = 'season'
      if (this.activeAnalyticsTab === 'ceremony') {
        this.loadSeasonCeremonyBreakdown({ force: true, loadInsights: true })
      }
    },
    onCeremonyInsightsModeChange() {
      if (this.ceremonyInsightsMode === 'season_comparison') {
        this.ensureCeremonyCompareSeasonKey()
      } else {
        this.ceremonySeasonComparePollRunId = Date.now()
        this.ceremonySeasonCompareJobStatus = ''
      }
      if (this.activeAnalyticsTab !== 'ceremony') return
      this.loadSeasonCeremonyBreakdown({ force: false, loadInsights: true })
    },
    onCeremonyCompareSeasonChange() {
      if (this.activeAnalyticsTab !== 'ceremony') return
      if (this.ceremonyInsightsMode !== 'season_comparison') return
      this.loadSeasonCeremonyComparison({ force: true })
    },
    refreshCeremonyTab() {
      this.ceremonyDataScope = 'season'
      this.ensureCeremonyCompareSeasonKey()
      this.loadSeasonCeremonyBreakdown({ force: true, loadInsights: true })
    },
    ensureCeremonyCompareSeasonKey() {
      const options = this.ceremonyCompareSeasonOptions
      if (!options.length) {
        this.ceremonyCompareSeasonKey = ''
        return
      }
      const currentKey = String(this.ceremonyCompareSeasonKey || '')
      if (options.some(s => s.key === currentKey)) return

      const seasons = Array.isArray(this.surveySeasons) ? this.surveySeasons : []
      const selectedIdx = seasons.findIndex(s => s.key === this.ceremonySeasonKey)
      const previous = selectedIdx >= 0 ? seasons[selectedIdx + 1] : null
      if (previous && options.some(s => s.key === previous.key)) {
        this.ceremonyCompareSeasonKey = previous.key
        return
      }
      this.ceremonyCompareSeasonKey = String(options[0]?.key || '')
    },
    seasonCompareMetricLabel(row) {
      return String(row?.label || row?.metric_key || 'Metric')
    },
    seasonCompareMetricChartValue(row) {
      const pct = Number(row?.delta_pct)
      if (Number.isFinite(pct)) return Math.abs(pct)
      const delta = Number(row?.delta)
      return Number.isFinite(delta) ? Math.abs(delta) : 0
    },
    seasonCompareMetricDeltaText(row) {
      const delta = Number(row?.delta || 0)
      const pct = Number(row?.delta_pct)
      const sign = delta > 0 ? '+' : ''
      if (Number.isFinite(pct)) return `${sign}${delta.toFixed(2)} (${sign}${pct.toFixed(2)}%)`
      return `${sign}${delta.toFixed(2)}`
    },
    formatComparisonMetricValue(value, kind = 'count') {
      const n = Number(value || 0)
      if (kind === 'amplification_index') return `${n.toFixed(2)}x`
      if (kind === 'rate') return n.toFixed(2)
      return Math.round(n).toLocaleString()
    },
    comparisonDeltaArrow(deltaPct) {
      const n = Number(deltaPct || 0)
      if (n > 0) return '▲'
      if (n < 0) return '▼'
      return '→'
    },
    comparisonDeltaClass(deltaPct) {
      const n = Number(deltaPct || 0)
      if (n > 0) return 'text-green-700'
      if (n < 0) return 'text-red-700'
      return 'text-gray-500'
    },
    comparisonDeltaText(deltaPct) {
      const n = Number(deltaPct || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${n.toFixed(2)}%`
    },
    comparisonDeltaPointsText(delta) {
      const n = Number(delta || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${n.toFixed(2)}pp`
    },
    comparisonDeltaCountText(delta) {
      const n = Number(delta || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}${Math.round(n).toLocaleString()}`
    },
    comparisonRelativeWidth(value, maxValue) {
      const n = Number(value || 0)
      const max = Number(maxValue || 0)
      if (!(max > 0)) return '0%'
      const pct = Math.max(0, Math.min(100, (100 * n) / max))
      return `${pct.toFixed(2)}%`
    },
    selectSurveyQuestion(questionKey) {
      this.surveySelectedQuestionKey = questionKey
      this.loadSurveyQuestionCeremony()
    },
    onSurveyCompareQuestionChange() {
      const comparisonCeremonyIds = this.comparisonCeremonyIdsForMode(this.surveyCompareMode)
      this.loadSurveyComparison({
        ceremonyIds: comparisonCeremonyIds,
        questionKey: this.surveyCompareQuestionKey,
        loadingScope: 'question',
      })
    },
    onSurveyCompareModeChange() {
      this.surveyComparisonRowsCollapsed = true
      this.surveyQuestionComparisonRowsCollapsed = true
      const previousSeasonKey = this.surveySeasonKey
      const comparisonCeremonyIds = this.comparisonCeremonyIdsForMode(this.surveyCompareMode)
      const forcedSeason = this.surveyCompareMode === 'ceremony' &&
        previousSeasonKey === 'all' &&
        this.surveySeasonKey !== 'all'
      if (forcedSeason) {
        this.loadSurvey()
        return
      }
      this.loadSurveyComparison({
        ceremonyIds: comparisonCeremonyIds,
        questionKey: this.surveyCompareQuestionKey,
        loadingScope: 'global',
      })
    },
    ensureSurveyCompareQuestionSelection(questionRows = null) {
      const rows = Array.isArray(questionRows) ? questionRows : this.surveyComparisonQuestions
      if (!rows.length) {
        this.surveyCompareQuestionKey = ''
        return
      }
      const exists = rows.find(q => String(q?.question_key || '') === String(this.surveyCompareQuestionKey || ''))
      if (!exists) {
        this.surveyCompareQuestionKey = String(rows[0]?.question_key || '')
      }
    },
    ensureSurveyExportSelection(questionRows = null) {
      const rows = Array.isArray(questionRows) ? questionRows : this.surveyExportQuestions
      if (!rows.length) {
        this.surveyExportQuestionKey = ''
        this.surveyExportAnswerValues = []
        return
      }
      const exists = rows.find(q => String(q?.question_key || '') === String(this.surveyExportQuestionKey || ''))
      if (!exists) {
        this.surveyExportQuestionKey = String(rows[0]?.question_key || '')
      }
      if (this.surveyExportPermissionQuestionKey) {
        const permissionExists = rows.find(q => String(q?.question_key || '') === String(this.surveyExportPermissionQuestionKey || ''))
        if (!permissionExists) {
          this.surveyExportPermissionQuestionKey = ''
          this.surveyExportPermissionAnswerValues = []
        }
      }
      this.syncSurveyExportAnswers()
      this.syncSurveyExportPermissionAnswers()
    },
    surveyExportModeLabel() {
      return 'full dataset export'
    },
    surveyExportNeedsQuestion() {
      return false
    },
    surveyExportNeedsAnswers() {
      return false
    },
    surveyExportUsesPermissionFilter() {
      return false
    },
    surveyExportUsesContactableFilter() {
      return false
    },
    syncSurveyExportAnswers() {
      const allowed = new Set((this.surveyExportQuestionOptions || []).map(o => String(o?.value || '')))
      this.surveyExportAnswerValues = (this.surveyExportAnswerValues || [])
        .map(v => String(v))
        .filter(v => allowed.has(v))
      if (this.surveyExportNeedsAnswers() && !this.surveyExportAnswerValues.length && this.surveyExportQuestionOptions.length) {
        this.surveyExportAnswerValues = [String(this.surveyExportQuestionOptions[0].value)]
      }
    },
    syncSurveyExportPermissionAnswers() {
      const allowed = new Set((this.surveyExportPermissionQuestionOptions || []).map(o => String(o?.value || '')))
      this.surveyExportPermissionAnswerValues = (this.surveyExportPermissionAnswerValues || [])
        .map(v => String(v))
        .filter(v => allowed.has(v))
    },
    onSurveyExportModeChange() {
      this.surveyExportError = ''
      this.surveyExportPreview = null
      this.surveyExportMode = 'full_dataset'
      this.surveyExportFieldPreset = 'full_response'
      this.surveyExportPermissionQuestionKey = ''
      this.surveyExportPermissionAnswerValues = []
    },
    onSurveyExportFieldPresetChange() {
      this.surveyExportError = ''
      this.surveyExportPreview = null
      this.surveyExportFieldPreset = 'full_response'
    },
    onSurveyExportQuestionChange() {
      this.surveyExportError = ''
      this.surveyExportPreview = null
      this.surveyExportAnswerValues = []
      this.syncSurveyExportAnswers()
    },
    onSurveyExportPermissionQuestionChange() {
      this.surveyExportError = ''
      this.surveyExportPreview = null
      this.surveyExportPermissionAnswerValues = []
      this.syncSurveyExportPermissionAnswers()
    },
    onSurveyExportAnswerChange(value, checked) {
      const set = new Set((this.surveyExportAnswerValues || []).map(v => String(v)))
      if (checked) set.add(String(value))
      else set.delete(String(value))
      this.surveyExportAnswerValues = Array.from(set)
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    onSurveyExportPermissionAnswerChange(value, checked) {
      const set = new Set((this.surveyExportPermissionAnswerValues || []).map(v => String(v)))
      if (checked) set.add(String(value))
      else set.delete(String(value))
      this.surveyExportPermissionAnswerValues = Array.from(set)
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    selectAllSurveyExportAnswers() {
      this.surveyExportAnswerValues = (this.surveyExportQuestionOptions || []).map(o => String(o?.value || '')).filter(Boolean)
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    selectAllSurveyExportPermissionAnswers() {
      this.surveyExportPermissionAnswerValues = (this.surveyExportPermissionQuestionOptions || []).map(o => String(o?.value || '')).filter(Boolean)
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    clearSurveyExportPermissionAnswers() {
      this.surveyExportPermissionAnswerValues = []
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    clearSurveyExportAnswers() {
      this.surveyExportAnswerValues = []
      this.surveyExportError = ''
      this.surveyExportPreview = null
    },
    configureSurveyExportTask(task) {
      this.surveyExportError = ''
      this.surveyExportPreview = null
      if (task === 'summary') {
        this.setSurveyAnalysisTab('overview')
        return
      }
      this.surveyExportMode = 'full_dataset'
      this.surveyExportFieldPreset = 'full_response'
      this.surveyExportPermissionQuestionKey = ''
      this.surveyExportPermissionAnswerValues = []
    },
    canPreviewSurveyExport() {
      return this.canDownloadSurveyExport()
    },
    canDownloadSurveyExport() {
      return this.canExportSurvey && !this.isAllScope && !!this.surveyStats && !this.surveyExportLoading
    },
    canRunSurveyExportTask(task) {
      return task === 'full_dataset' && this.canDownloadSurveyExport()
    },
    surveyExportPayload(preview = false) {
      const payload = {
        universitySlug: this.selectedSlug,
        ceremonyIds: (this.surveySelectedCeremonyIds || []).map(v => String(v)),
        exportMode: 'full_dataset',
        fieldPreset: 'full_response',
        preview,
      }
      return payload
    },
    async requestSurveyExport(preview = false) {
      const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
      const url = base + '/stats/survey/export'
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const r = await axios.post(url, this.surveyExportPayload(preview), { headers })
      return r.data || {}
    },
    async previewSurveyExportTask(task) {
      this.configureSurveyExportTask(task)
      await this.$nextTick()
      await this.previewSurveyExport()
    },
    async downloadSurveyExportTask(task) {
      this.configureSurveyExportTask(task)
      await this.$nextTick()
      await this.downloadSurveyExport()
    },
    async previewSurveyExport() {
      if (!this.canPreviewSurveyExport()) return
      this.surveyExportPreviewLoading = true
      this.surveyExportError = ''
      try {
        this.surveyExportPreview = await this.requestSurveyExport(true)
      } catch (e) {
        try { this.surveyExportError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Preview failed' } catch { this.surveyExportError = 'Preview failed' }
      } finally {
        this.surveyExportPreviewLoading = false
      }
    },
    async downloadSurveyExport() {
      if (!this.canDownloadSurveyExport()) return
      this.surveyExportLoading = true
      this.surveyExportError = ''
      try {
        const data = await this.requestSurveyExport(false)
        if (!data.csv_base64) throw new Error('Missing CSV data')
        const binary = atob(data.csv_base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes], { type: data.content_type || 'text/csv;charset=utf-8' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = data.filename || `${this.selectedSlug}-survey-export.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(link.href), 1000)
        this.surveyExportPreview = {
          row_count: data.row_count,
          columns: data.columns,
          mode: data.mode,
          field_preset: data.field_preset,
        }
        trackAdminEvent('admin_action_completed', {
          toolKey: 'engagement_analytics',
          metadata: {
            action: 'download_survey_export',
            universitySlug: this.selectedSlug,
            rowCount: data.row_count,
          },
        })
      } catch (e) {
        try { this.surveyExportError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Export failed' } catch { this.surveyExportError = 'Export failed' }
      } finally {
        this.surveyExportLoading = false
      }
    },
    async downloadSurveyReport() {
      if (this.isAllScope) {
        this.surveyReportError = 'Select a university to download a survey report.'
        return
      }
      if (!this.surveyStats) {
        this.surveyReportError = 'Load survey analytics before downloading a report.'
        return
      }
      this.surveyReportLoading = true
      this.surveyReportError = ''
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/reports/survey'
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const payload = {
          universitySlug: this.selectedSlug,
          universityName: this.selectedUniversityName,
          reportPack: this.surveyReportPack || 'executive',
          reportDateLabel: this.formatDateLabel(Date.now()),
          dateRangeLabel: this.surveySeasonKey === 'all'
            ? 'Selected ceremonies'
            : String((this.surveySeasons || []).find(s => s.key === this.surveySeasonKey)?.label || 'Selected season'),
          scopeLabel: this.surveyCeremonySelectionLabel,
          ceremonyIds: (this.surveySelectedCeremonyIds || []).map(v => String(v)),
        }
        const r = await axios.post(url, payload, { headers })
        const data = r.data || {}
        if (!data.pdf_base64) throw new Error('Missing PDF data')
        const binary = atob(data.pdf_base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes], { type: data.content_type || 'application/pdf' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = data.filename || `${this.selectedSlug}-${this.surveyReportPack || 'executive'}-survey-report.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(link.href), 1000)
        trackAdminEvent('admin_action_completed', {
          toolKey: 'engagement_analytics',
          metadata: {
            action: 'download_survey_report',
            universitySlug: this.selectedSlug,
            reportPack: this.surveyReportPack || 'executive',
          },
        })
      } catch (e) {
        try { this.surveyReportError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Report download failed' } catch { this.surveyReportError = 'Report download failed' }
      } finally {
        this.surveyReportLoading = false
      }
    },
    surveyOptionWidth(option) {
      const pct = Number(option?.percent_of_answered || 0)
      return `${Math.max(0, Math.min(100, pct)).toFixed(2)}%`
    },
    isBinaryQuestion(question) {
      const options = Array.isArray(question?.options) ? question.options : []
      if (options.length !== 2) return false
      const normalized = options
        .map(o => String(o?.label || o?.value || '').trim().toLowerCase())
        .sort()
      return normalized.join('|') === 'no|yes' || normalized.join('|') === 'false|true'
    },
    ceremonyIdsForSeasonKey(seasonKey) {
      if (seasonKey === 'all') {
        return (this.surveyCeremonies || []).map(c => String(c.id))
      }
      const season = (this.surveySeasons || []).find(s => s.key === seasonKey)
      return season ? [...season.ceremonyIds] : []
    },
    comparisonCeremonyIdsForMode(mode, selectedCeremonyIds = null, allCeremonyIds = null) {
      const resolvedMode = mode || this.surveyCompareMode
      const resolvedAllIds = Array.isArray(allCeremonyIds)
        ? allCeremonyIds.map(v => String(v))
        : (this.surveyCeremonies || []).map(c => String(c.id))
      if (resolvedMode === 'cohort') {
        return resolvedAllIds
      }
      const seasonKey = this.surveySeasonKey
      if (seasonKey && seasonKey !== 'all') {
        return this.ceremonyIdsForSeasonKey(seasonKey)
      }
      const resolvedSelectedIds = Array.isArray(selectedCeremonyIds)
        ? selectedCeremonyIds.map(v => String(v))
        : (this.surveySelectedCeremonyIds || []).map(v => String(v))
      return resolvedSelectedIds.length ? resolvedSelectedIds : resolvedAllIds
    },
    formatSurveyCeremonyOption(ceremony) {
      if (!ceremony) return ''
      const dt = ceremony.datetime ? this.ceremonyTitle({ ceremony_datetime: ceremony.datetime, ceremony_label: null, ceremony_id: ceremony.id }) : String(ceremony.id)
      if (ceremony.label && ceremony.label.trim()) return `${dt} - ${ceremony.label}`
      return dt
    },
    async onSurveySeasonChange() {
      this.surveySelectedCeremonyIds = this.ceremonyIdsForSeasonKey(this.surveySeasonKey)
      this.surveyCeremonySelectionDraft = [...this.surveySelectedCeremonyIds]
      this.surveyCeremonyDropdownOpen = false
      await this.loadSurvey()
      if (this.surveyAnalysisTab === 'insights' && !this.isAllScope) {
        await this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: false })
      }
    },
    async onSurveyCeremoniesChange() {
      const ids = Array.isArray(this.surveySelectedCeremonyIds) ? this.surveySelectedCeremonyIds.map(v => String(v)) : []
      this.surveySelectedCeremonyIds = ids
      this.surveyCeremonySelectionDraft = [...ids]
      await this.loadSurvey()
      if (this.surveyAnalysisTab === 'insights' && !this.isAllScope) {
        await this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: false })
      }
    },
    async onSurveyInsightsSegmentByChange() {
      if (this.surveyAnalysisTab !== 'insights' || this.isAllScope) return
      await this.loadSurveyInsights({ includeNarrative: true, silent: false, forceRefresh: false })
    },
    toggleSurveyCeremonyDropdown() {
      if (this.surveyCeremonyDropdownOpen) {
        this.closeSurveyCeremonyDropdown()
        return
      }
      this.surveyCeremonySelectionDraft = [...(this.surveySelectedCeremonyIds || []).map(v => String(v))]
      this.surveyCeremonyDropdownOpen = true
    },
    closeSurveyCeremonyDropdown() {
      this.surveyCeremonyDropdownOpen = false
      this.surveyCeremonySelectionDraft = [...(this.surveySelectedCeremonyIds || []).map(v => String(v))]
    },
    onSurveyCeremonyDraftChange(ceremonyId, checked) {
      const id = String(ceremonyId || '')
      if (!id) return
      const set = new Set((this.surveyCeremonySelectionDraft || []).map(v => String(v)))
      if (checked) set.add(id)
      else set.delete(id)
      this.surveyCeremonySelectionDraft = [...set]
    },
    selectAllSurveyCeremonyDraft() {
      this.surveyCeremonySelectionDraft = this.surveyCeremonyOptionsForControl.map(c => String(c.id))
    },
    clearSurveyCeremonyDraft() {
      this.surveyCeremonySelectionDraft = []
    },
    applySurveyCeremonySelection() {
      const options = new Set(this.surveyCeremonyOptionsForControl.map(c => String(c.id)))
      const nextIds = (this.surveyCeremonySelectionDraft || [])
        .map(v => String(v))
        .filter(id => options.has(id))
      this.surveySelectedCeremonyIds = nextIds
      this.surveyCeremonyDropdownOpen = false
      this.onSurveyCeremoniesChange()
    },
    handleDocumentClick(event) {
      if (!this.surveyCeremonyDropdownOpen) return
      const root = this.$refs?.surveyCeremonyDropdown
      const target = event?.target
      if (!root || !target) return
      if (root.contains(target)) return
      this.closeSurveyCeremonyDropdown()
    },
    compactInsightSummary(text, maxChars = 140) {
      const value = this.formatInsightText(text)
      if (!value) return ''
      if (value.length <= maxChars) return value
      return `${value.slice(0, Math.max(0, maxChars - 1)).trim()}...`
    },
    insightOrdinalDay(day) {
      const n = Number(day || 0)
      if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`
      const last = n % 10
      if (last === 1) return `${n}st`
      if (last === 2) return `${n}nd`
      if (last === 3) return `${n}rd`
      return `${n}th`
    },
    formatInsightIsoDate(isoText) {
      const raw = String(isoText || '').trim()
      if (!raw) return raw
      const dt = new Date(raw)
      if (!Number.isFinite(dt.getTime())) return raw
      const day = this.insightOrdinalDay(dt.getDate())
      const month = dt.toLocaleString('en-GB', { month: 'long' })
      const year = dt.getFullYear()
      const hasTime = /T\d{2}:\d{2}/.test(raw)
      if (!hasTime) return `${day} ${month} ${year}`
      const hours = String(dt.getHours()).padStart(2, '0')
      const minutes = String(dt.getMinutes()).padStart(2, '0')
      return `${day} ${month} ${year}, ${hours}:${minutes}`
    },
    formatInsightText(value) {
      const text = String(value || '').trim()
      if (!text) return ''
      const isoDatePattern = /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?\b/g
      return text.replace(isoDatePattern, (match) => this.formatInsightIsoDate(match))
    },
    formatInsightsGeneratedAt(value) {
      const text = String(value || '').trim()
      if (!text) return ''
      const dt = new Date(text)
      if (!Number.isFinite(dt.getTime())) return text
      return dt.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    insightConfidenceLabel(card) {
      const confidence = String(card?.confidence || '').toLowerCase()
      if (confidence === 'low') return 'Low sample'
      if (confidence === 'medium') return 'Confidence: medium'
      if (confidence === 'high') return 'Confidence: high'
      return 'Confidence: unknown'
    },
    insightCategory(card) {
      const category = String(card?.category || '').toLowerCase()
      if (category === 'participation' || category === 'completion' || category === 'distribution') return category
      return 'information'
    },
    insightCategoryLabel(card) {
      const category = this.insightCategory(card)
      if (category === 'participation') return 'Participation'
      if (category === 'completion') return 'Completion'
      if (category === 'distribution') return 'Distribution'
      return 'Information'
    },
    insightTone(card) {
      const visualTone = String(card?.visual?.tone || '').toLowerCase()
      if (visualTone === 'positive') {
        return {
          container: 'border-green-200 bg-green-50/60',
          textStrong: 'text-green-800',
          pill: 'text-green-700 border-green-300',
          barColor: '#16a34a',
          link: 'text-green-700',
        }
      }
      if (visualTone === 'attention') {
        return {
          container: 'border-amber-200 bg-amber-50/60',
          textStrong: 'text-amber-800',
          pill: 'text-amber-700 border-amber-300',
          barColor: '#f59e0b',
          link: 'text-amber-700',
        }
      }
      if (visualTone === 'neutral') {
        return {
          container: 'border-slate-200 bg-slate-50/70',
          textStrong: 'text-slate-700',
          pill: 'text-slate-700 border-slate-300',
          barColor: '#64748b',
          link: 'text-slate-700',
        }
      }
      const category = this.insightCategory(card)
      if (category === 'participation') {
        return {
          container: 'border-green-200 bg-green-50/60',
          textStrong: 'text-green-800',
          pill: 'text-green-700 border-green-300',
          barColor: '#16a34a',
          link: 'text-green-700',
        }
      }
      if (category === 'completion') {
        return {
          container: 'border-amber-200 bg-amber-50/60',
          textStrong: 'text-amber-800',
          pill: 'text-amber-700 border-amber-300',
          barColor: '#f59e0b',
          link: 'text-amber-700',
        }
      }
      if (category === 'distribution') {
        return {
          container: 'border-slate-200 bg-slate-50/70',
          textStrong: 'text-slate-700',
          pill: 'text-slate-700 border-slate-300',
          barColor: '#64748b',
          link: 'text-slate-700',
        }
      }
      return {
        container: 'border-indigo-200 bg-indigo-50/60',
        textStrong: 'text-indigo-800',
        pill: 'text-indigo-700 border-indigo-300',
        barColor: '#6366f1',
        link: 'text-indigo-700',
      }
    },
    insightStrengthPercent(card) {
      const confidence = String(card?.confidence || '').toLowerCase()
      if (confidence === 'high') return 85
      if (confidence === 'medium') return 60
      if (confidence === 'low') return 35
      return 50
    },
    parseInsightMetricValue(value) {
      const text = String(value || '').trim()
      if (!text) return { kind: 'unknown', number: null }
      const percentMatch = text.match(/([+-]?\d+(?:\.\d+)?)\s*%/)
      if (percentMatch) {
        const n = Number(percentMatch[1])
        return { kind: 'percent', number: Number.isFinite(n) ? n : null }
      }
      const ppMatch = text.match(/([+-]?\d+(?:\.\d+)?)\s*pp/i)
      if (ppMatch) {
        const n = Number(ppMatch[1])
        return { kind: 'pp', number: Number.isFinite(n) ? n : null }
      }
      const digits = text.replace(/,/g, '').match(/([+-]?\d+(?:\.\d+)?)/)
      if (digits) {
        const n = Number(digits[1])
        if (Number.isFinite(n)) return { kind: 'count', number: n }
      }
      return { kind: 'unknown', number: null }
    },
    insightPrimaryMetric(card) {
      const typed = card?.primary_metric
      if (typed && typeof typed === 'object' && (typed.value_display !== undefined || typed.value_number !== undefined)) {
        const rawKind = String(typed.kind || '').toLowerCase()
        const kind = ['percent', 'pp', 'count'].includes(rawKind) ? rawKind : 'unknown'
        const valueNumber = Number(typed.value_number)
        return {
          label: String(typed.label || 'Metric'),
          value: String(
            typed.value_display !== undefined && typed.value_display !== null
              ? typed.value_display
              : (Number.isFinite(valueNumber) ? valueNumber : '')
          ),
          kind,
          number: Number.isFinite(valueNumber) ? valueNumber : null,
        }
      }
      const evidence = Array.isArray(card?.evidence) ? card.evidence : []
      const ranked = [
        ...evidence.filter(ev => /rate|delta|divergence|completion|response/i.test(String(ev?.label || ''))),
        ...evidence.filter(ev => /answered|eligible|responders|unanswered|count/i.test(String(ev?.label || ''))),
        ...evidence,
      ]
      const seen = new Set()
      const unique = ranked.filter((ev) => {
        const key = `${String(ev?.label || '')}:${String(ev?.value || '')}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      const primary = unique[0] || {}
      const label = String(primary?.label || 'Metric')
      const value = String(primary?.value || '')
      const parsed = this.parseInsightMetricValue(value)
      return {
        label,
        value,
        kind: parsed.kind,
        number: parsed.number,
      }
    },
    insightMetricBarPercent(metric, card, maxCount = 0) {
      const typedBarPercent = Number(card?.visual?.bar_percent)
      if (Number.isFinite(typedBarPercent)) {
        return Math.max(0, Math.min(100, typedBarPercent))
      }
      const kind = String(metric?.kind || '')
      const number = Number(metric?.number)
      if (kind === 'percent' && Number.isFinite(number)) {
        return Math.max(8, Math.min(100, Math.abs(number)))
      }
      if (kind === 'pp' && Number.isFinite(number)) {
        return Math.max(8, Math.min(100, Math.abs(number) * 4))
      }
      if (kind === 'count' && Number.isFinite(number)) {
        if (maxCount > 0) {
          return Math.max(8, Math.min(100, (100 * Math.abs(number)) / maxCount))
        }
        const logScale = Math.log10(Math.abs(number) + 1) * 24
        return Math.max(8, Math.min(100, logScale))
      }
      return this.insightStrengthPercent(card)
    },
    toggleEngagementDeterministicInsights() {
      this.engagementDeterministicInsightsCollapsed = !this.engagementDeterministicInsightsCollapsed
    },
    toggleCeremonyDeterministicInsights() {
      this.ceremonyDeterministicInsightsCollapsed = !this.ceremonyDeterministicInsightsCollapsed
    },
    toggleSurveyInsightsCaveats() {
      this.surveyInsightsCaveatsCollapsed = !this.surveyInsightsCaveatsCollapsed
    },
    insightCardId(card, idx, prefix = 'insight') {
      const id = String(card?.id || '').trim()
      if (id) return id
      return `${prefix}-${idx}`
    },
    isInsightCardExpanded(card, idx, prefix = 'insight') {
      const id = this.insightCardId(card, idx, prefix)
      if (Object.prototype.hasOwnProperty.call(this.surveyInsightCardExpanded || {}, id)) {
        return Boolean(this.surveyInsightCardExpanded?.[id])
      }
      return prefix === 'top' || prefix === 'key'
    },
    toggleInsightCard(card, idx, prefix = 'insight') {
      const id = this.insightCardId(card, idx, prefix)
      const current = Boolean(this.surveyInsightCardExpanded?.[id])
      this.$set(this.surveyInsightCardExpanded, id, !current)
    },
    isDetailedInsightExpanded(card, idx) {
      const id = this.insightCardId(card, idx, 'detail')
      return Boolean(this.surveyDetailedInsightExpanded?.[id])
    },
    toggleDetailedInsight(card, idx) {
      const id = this.insightCardId(card, idx, 'detail')
      const current = Boolean(this.surveyDetailedInsightExpanded?.[id])
      this.$set(this.surveyDetailedInsightExpanded, id, !current)
    },
    async refreshSurveyCeremonies() {
      this.surveyCeremonies = []
      this.surveySeasonKey = 'all'
      this.surveySelectedCeremonyIds = []
      this.surveyCeremonySelectionDraft = []
      this.surveyCeremonyDropdownOpen = false
      this.ceremonySeasonKey = 'all'
      this.ceremonyCompareSeasonKey = ''
      this.ceremonySelectedCeremonyIds = []
      if (this.isAllScope) return
      const universityId = this.selectedUniversity?.id
      if (!universityId) return
      const r = await axios.get(`/universities/${universityId}/ceremonies`)
      const rows = Array.isArray(r.data) ? r.data : []
      this.surveyCeremonies = rows
        .map((row) => {
          const id = row?.id ? String(row.id) : ''
          const dt = row?.datetime || null
          const ts = dt ? new Date(dt).getTime() : Number.NaN
          return {
            id,
            label: row?.label || null,
            datetime: dt,
            sortTs: Number.isFinite(ts) ? ts : Number.NaN,
          }
        })
        .filter(c => c.id)

      if (!this.surveyCeremonies.length) return

      const seasons = this.surveySeasons
      if (!seasons.length) {
        this.surveySelectedCeremonyIds = this.surveyCeremonies.map(c => String(c.id))
        this.surveyCeremonySelectionDraft = [...this.surveySelectedCeremonyIds]
        this.ceremonySelectedCeremonyIds = [...this.surveySelectedCeremonyIds]
        this.ceremonyCompareSeasonKey = ''
        return
      }

      const existing = seasons.find(s => s.key === this.surveySeasonKey)
      if (existing) {
        this.surveySelectedCeremonyIds = this.ceremonyIdsForSeasonKey(this.surveySeasonKey)
      } else {
        this.surveySeasonKey = seasons[0].key
        this.surveySelectedCeremonyIds = [...seasons[0].ceremonyIds]
      }
      this.surveyCeremonySelectionDraft = [...this.surveySelectedCeremonyIds]

      const ceremonyExisting = seasons.find(s => s.key === this.ceremonySeasonKey)
      if (ceremonyExisting) {
        this.ceremonySelectedCeremonyIds = this.ceremonyIdsForSeasonKey(this.ceremonySeasonKey)
      } else {
        this.ceremonySeasonKey = seasons[0].key
        this.ceremonySelectedCeremonyIds = [...seasons[0].ceremonyIds]
      }
      this.ensureCeremonyCompareSeasonKey()
    },
    geoWidth(count) {
      const max = Math.max(...(this.stats.top_timezones || []).map(r => r.count), 1)
      return (100 * count / max).toFixed(2) + '%'
    },
    metricValue(row, metric) {
      return Number(row?.[metric] || 0)
    },
    ceremonyMetricWidth(row, metric) {
      const rows = this.ceremonyRowsSorted
      const max = Math.max(...rows.map(r => this.metricValue(r, metric)), 1)
      return ((100 * this.metricValue(row, metric)) / max).toFixed(2) + '%'
    },
    surveyMetricValue(row, metric) {
      return Number(row?.[metric] || 0)
    },
    surveyMetricWidth(row, metric) {
      const rows = this.surveyCeremonyRowsSorted
      const max = Math.max(...rows.map(r => this.surveyMetricValue(r, metric)), 1)
      return ((100 * this.surveyMetricValue(row, metric)) / max).toFixed(2) + '%'
    },
    surveyQuestionMetricValue(row, metric) {
      return Number(row?.[metric] || 0)
    },
    surveyQuestionMetricWidth(row, metric) {
      const rows = this.surveyQuestionCeremonyRowsSorted
      const max = Math.max(...rows.map(r => this.surveyQuestionMetricValue(r, metric)), 1)
      return ((100 * this.surveyQuestionMetricValue(row, metric)) / max).toFixed(2) + '%'
    },
    surveyComparisonMetricValue(row) {
      return Number(row?.[this.surveyCompareMetric] || 0)
    },
    surveyComparisonMetricDisplay(row) {
      const value = this.surveyComparisonMetricValue(row)
      if (this.surveyCompareMetric === 'response_rate_pct' || this.surveyCompareMetric === 'question_completion_rate_pct') {
        return `${value}%`
      }
      return String(value)
    },
    surveyComparisonDeltaValue(row) {
      if (this.surveyCompareMetric === 'question_completion_rate_pct') {
        return Number(row?.delta_question_completion_rate_pct || 0)
      }
      return Number(row?.delta_response_rate_pct || 0)
    },
    surveyComparisonRowTitle(row) {
      if (this.surveyCompareMode === 'cohort') return row?.label || row?.compare_key || 'Unknown cohort'
      return this.ceremonyTitle(row)
    },
    surveyComparisonRowChartLabel(row) {
      if (this.surveyCompareMode === 'cohort') return row?.label || row?.compare_key || 'Unknown cohort'
      if (row?.ceremony_datetime) {
        try {
          return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(row.ceremony_datetime))
        } catch (_) {
          return this.surveyComparisonRowTitle(row)
        }
      }
      return this.surveyComparisonRowTitle(row)
    },
    surveyQuestionComparisonMetricValue(row) {
      return Number(row?.[this.surveyQuestionCompareMetric] || 0)
    },
    surveyDeltaClass(delta) {
      if (Number(delta || 0) > 0) return 'text-green-700'
      if (Number(delta || 0) < 0) return 'text-red-700'
      return 'text-gray-600'
    },
    formatSurveyDelta(delta) {
      const val = Number(delta || 0)
      const sign = val > 0 ? '+' : ''
      return `${sign}${val.toFixed(2)}pp`
    },
    tooltipTextForElement(el) {
      if (!el) return ''
      return String(el.getAttribute('title') || el.getAttribute('data-tip') || '').trim()
    },
    showFloatingTooltip(el) {
      const text = this.tooltipTextForElement(el)
      if (!text) {
        this.hideFloatingTooltip()
        return
      }
      this.activeInfoTipEl = el
      this.floatingTooltip = {
        ...this.floatingTooltip,
        visible: true,
        text,
      }
      this.$nextTick(() => {
        this.positionFloatingTooltip(el)
      })
    },
    positionFloatingTooltip(el) {
      if (!el || !this.floatingTooltip.visible) return
      const tip = this.$refs.floatingInfoTooltip
      if (!tip) return
      const rect = el.getBoundingClientRect()
      const tipRect = tip.getBoundingClientRect()
      const viewportW = window.innerWidth || document.documentElement.clientWidth || 0
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 0
      const margin = 8
      let left = rect.left + (rect.width / 2)
      const minLeft = margin + (tipRect.width / 2)
      const maxLeft = Math.max(minLeft, viewportW - margin - (tipRect.width / 2))
      left = Math.max(minLeft, Math.min(maxLeft, left))

      let top = rect.top - tipRect.height - 10
      if (top < margin) {
        top = rect.bottom + 10
      }
      if ((top + tipRect.height) > (viewportH - margin)) {
        top = Math.max(margin, viewportH - margin - tipRect.height)
      }

      this.floatingTooltip = {
        ...this.floatingTooltip,
        left: Math.round(left),
        top: Math.round(top),
      }
    },
    hideFloatingTooltip() {
      this.activeInfoTipEl = null
      if (!this.floatingTooltip.visible) return
      this.floatingTooltip = {
        ...this.floatingTooltip,
        visible: false,
      }
    },
    onInfoTipOver(event) {
      const el = event?.target?.closest ? event.target.closest('.info-tip') : null
      if (!el || !this.$el.contains(el)) return
      if (this.activeInfoTipEl === el && this.floatingTooltip.visible) {
        this.positionFloatingTooltip(el)
        return
      }
      this.showFloatingTooltip(el)
    },
    onInfoTipOut(event) {
      if (!this.activeInfoTipEl) return
      const related = event?.relatedTarget
      if (related && this.activeInfoTipEl.contains && this.activeInfoTipEl.contains(related)) return
      if (related && related.closest && related.closest('.info-tip') === this.activeInfoTipEl) return
      const nextTip = related && related.closest ? related.closest('.info-tip') : null
      if (nextTip && this.$el.contains(nextTip)) {
        this.showFloatingTooltip(nextTip)
        return
      }
      this.hideFloatingTooltip()
    },
    onInfoTipFocusIn(event) {
      const el = event?.target?.closest ? event.target.closest('.info-tip') : null
      if (!el || !this.$el.contains(el)) return
      this.showFloatingTooltip(el)
    },
    onInfoTipFocusOut(event) {
      if (!this.activeInfoTipEl) return
      const related = event?.relatedTarget
      if (related && related.closest && related.closest('.info-tip') === this.activeInfoTipEl) return
      this.hideFloatingTooltip()
    },
    sameIdSet(a, b) {
      const left = [...new Set((a || []).map(v => String(v)))]
      const right = [...new Set((b || []).map(v => String(v)))]
      if (left.length !== right.length) return false
      const rightSet = new Set(right)
      return left.every(v => rightSet.has(v))
    },
    ceremonyTitle(row) {
      if (!row) return ''
      if (row.ceremony_label) return row.ceremony_label
      if (row.ceremony_datetime) {
        try {
          return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(row.ceremony_datetime))
        } catch (_) {
          return row.ceremony_datetime
        }
      }
      return row.ceremony_id || 'Unknown ceremony'
    },
    isUnmappedCeremonyRow(row) {
      const id = String(row?.ceremony_id || '').trim().toLowerCase()
      const label = String(row?.ceremony_label || '').trim().toLowerCase()
      if (id.includes('unmapped') || label.includes('unmapped')) return true
      if (!id && !row?.ceremony_label && !row?.ceremony_datetime) return true
      return false
    },
    isCeremonyExpanded(ceremonyId) {
      return Boolean(this.expandedCeremonies?.[ceremonyId])
    },
    isCeremonyTopClipsLoading(ceremonyId) {
      return Boolean(this.ceremonyTopClipsLoading?.[ceremonyId])
    },
    async ensureCeremonyTopClips(row) {
      const ceremonyId = String(row?.ceremony_id || '').trim()
      if (!ceremonyId) return
      const existing = Array.isArray(row?.top_clips) ? row.top_clips : []
      if (existing.length) return
      if (this.ceremonyTopClipsLoading[ceremonyId]) {
        await this.ceremonyTopClipsLoading[ceremonyId]
        return
      }
      const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
      const url = base + '/stats/university'
      const payload = {
        universitySlug: this.selectedSlug,
        mode: 'ceremony_top_clips',
        ceremonyIds: [ceremonyId],
      }
      if (this.ceremonyDataScope === 'window') {
        if (this.start) payload.start = this.start
        if (this.end) payload.end = this.end
      }
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const source = this.startTopClipRequestSource(ceremonyId)
      const req = axios.post(url, payload, { headers, cancelToken: source.token })
        .then(async (r) => {
          const clips = Array.isArray(r?.data?.top_clips) ? r.data.top_clips : []
          this.$set(row, 'top_clips', clips)
          await Promise.all(clips.map(c => this.ensureClipName(c?.submission_id)))
        })
        .catch((e) => {
          if (this.isRequestCancelled(e)) return
          this.$set(row, 'top_clips', [])
        })
        .finally(() => {
          this.finishTopClipRequestSource(ceremonyId, source)
          this.$delete(this.ceremonyTopClipsLoading, ceremonyId)
        })
      this.$set(this.ceremonyTopClipsLoading, ceremonyId, req)
      await req
    },
    async toggleCeremonyExpand(row) {
      const ceremonyId = row?.ceremony_id
      if (!ceremonyId) return
      const expanded = this.isCeremonyExpanded(ceremonyId)
      this.$set(this.expandedCeremonies, ceremonyId, !expanded)
      if (!expanded) {
        await this.ensureCeremonyTopClips(row)
        const clips = Array.isArray(row?.top_clips) ? row.top_clips : []
        await Promise.all(clips.map(c => this.ensureClipName(c?.submission_id)))
      }
    },
    clipName(clip) {
      const submissionId = clip?.submission_id
      if (!submissionId) return 'Unknown graduate'
      return this.clipNameBySubmission[submissionId] || `Clip ${submissionId.slice(0, 8)}`
    },
    async ensureClipName(submissionId) {
      if (!submissionId) return
      if (this.clipNameBySubmission[submissionId]) return
      if (this.clipNameInflight[submissionId]) return this.clipNameInflight[submissionId]
      const token = localStorage.getItem('token') || ''
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      const req = axios
        .get(`/clips/${submissionId}`, { headers })
        .then((r) => {
          const n = r?.data?.registration?.name
          this.$set(this.clipNameBySubmission, submissionId, n || `Clip ${submissionId.slice(0, 8)}`)
        })
        .catch(() => {
          this.$set(this.clipNameBySubmission, submissionId, `Clip ${submissionId.slice(0, 8)}`)
        })
        .finally(() => {
          this.$delete(this.clipNameInflight, submissionId)
        })
      this.$set(this.clipNameInflight, submissionId, req)
      return req
    },
    async prefetchCeremonyNames() {
      if (this.activeAnalyticsTab === 'ceremony' && this.ceremonyInsightsMode === 'season_comparison') {
        return
      }
      const rows = this.ceremonyRowsBase
      const ids = []
      for (const row of rows) {
        const top = row?.top_clips?.[0]
        if (top?.submission_id) ids.push(top.submission_id)
      }
      const limited = ids.slice(0, 80)
      await Promise.all(limited.map(id => this.ensureClipName(id)))
    },
    async loadSeasonCeremonyInsights({ ceremonyIds = null, force = false } = {}) {
      if (this.selectedSlug === '_ALL_') return
      let ids = Array.isArray(ceremonyIds) ? ceremonyIds.map(v => String(v)) : (this.ceremonySelectedCeremonyIds || []).map(v => String(v))
      if (!ids.length) ids = (this.surveyCeremonies || []).map(c => String(c.id))
      const key = `${this.selectedSlug}::${[...ids].sort().join(',')}`
      if (!force && this.ceremonySeasonScopeKey === key && this.ceremonySeasonScopedInsights) return
      this.ceremonySeasonInsightsLoading = true
      this.ceremonySeasonInsightsError = ''
      let source = null
      try {
        this.ceremonyDeterministicInsightsCollapsed = true
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payload = {
          universitySlug: this.selectedSlug,
          mode: 'engagement_insights',
          ceremonyIds: ids,
          includeNarrative: true,
        }
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('ceremonySeasonInsights')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.ceremonySeasonScopedInsights = r?.data?.engagement_insights || null
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.ceremonySeasonScopedInsights = null
        try {
          this.ceremonySeasonInsightsError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Insights unavailable'
        } catch (_) {
          this.ceremonySeasonInsightsError = 'Insights unavailable'
        }
      } finally {
        this.finishRequestSource('ceremonySeasonInsights', source)
        this.ceremonySeasonInsightsLoading = false
      }
    },
    async loadSeasonCeremonyComparison({ ceremonyIds = null, force = false } = {}) {
      if (this.selectedSlug === '_ALL_') return
      if (this.ceremonyInsightsMode !== 'season_comparison') return
      if (this.ceremonySeasonKey === 'all') {
        this.ceremonySeasonCompareData = null
        this.ceremonySeasonCompareError = ''
        this.ceremonySeasonCompareJobStatus = ''
        return
      }
      this.ensureCeremonyCompareSeasonKey()
      const compareKey = String(this.ceremonyCompareSeasonKey || '')
      const compareSeason = (this.surveySeasons || []).find(s => s.key === compareKey)
      if (!compareSeason) {
        this.ceremonySeasonCompareData = null
        this.ceremonySeasonCompareError = ''
        this.ceremonySeasonCompareJobStatus = ''
        return
      }

      let ids = Array.isArray(ceremonyIds) ? ceremonyIds.map(v => String(v)) : (this.ceremonySelectedCeremonyIds || []).map(v => String(v))
      if (!ids.length) ids = this.ceremonyIdsForSeasonKey(this.ceremonySeasonKey)
      const compareIds = (compareSeason.ceremonyIds || []).map(v => String(v))
      if (!ids.length || !compareIds.length) {
        this.ceremonySeasonCompareData = null
        this.ceremonySeasonCompareError = ''
        this.ceremonySeasonCompareJobStatus = ''
        return
      }

      const key = `${this.selectedSlug}::${this.ceremonySeasonKey}::${compareKey}::${[...ids].sort().join(',')}::${[...compareIds].sort().join(',')}`
      if (!force && this.ceremonySeasonCompareScopeKey === key && this.ceremonySeasonCompareData) return

      const pollRunId = Date.now()
      this.ceremonySeasonComparePollRunId = pollRunId
      this.ceremonySeasonCompareLoading = true
      this.ceremonySeasonCompareError = ''
      this.ceremonySeasonCompareJobStatus = 'submitting'
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const createUrl = base + '/stats/engagement/comparison/jobs'
        const payload = {
          universitySlug: this.selectedSlug,
          ceremonyIds: ids,
          comparisonCeremonyIds: compareIds,
          currentSeasonLabel: this.ceremonySeasonScopeLabel,
          comparisonSeasonLabel: String(compareSeason.label || 'Comparison season'),
          forceRefresh: Boolean(force),
        }
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('ceremonySeasonCompare')
        const createRes = await axios.post(createUrl, payload, { headers, cancelToken: source.token })
        if (pollRunId !== this.ceremonySeasonComparePollRunId) return
        const createData = createRes?.data || {}
        this.ceremonySeasonCompareJobStatus = String(createData.status || 'queued')
        let nextData = null
        if (createData && createData.job_id) {
          const jobResult = await this.pollEngagementComparisonJob({
            base,
            headers,
            jobId: String(createData.job_id),
            pollRunId,
          })
          if (pollRunId !== this.ceremonySeasonComparePollRunId) return
          if (!jobResult || !jobResult.result) {
            const failedMessage = String(jobResult?.message || '').trim()
            this.ceremonySeasonCompareError = failedMessage || 'Season comparison unavailable'
            this.ceremonySeasonCompareData = null
            this.ceremonySeasonCompareJobStatus = String(jobResult?.status || 'failed')
            return
          }
          this.ceremonySeasonCompareJobStatus = String(jobResult.status || 'completed')
          nextData = jobResult.result?.engagement_season_compare || jobResult.result || null
        } else {
          this.ceremonySeasonCompareJobStatus = 'completed'
          nextData = createData?.engagement_season_compare || createData || null
        }
        this.ceremonySeasonCompareData = nextData
        this.ceremonySeasonCompareScopeKey = key
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.ceremonySeasonCompareData = null
        this.ceremonySeasonCompareJobStatus = 'failed'
        try {
          this.ceremonySeasonCompareError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Season comparison unavailable'
        } catch (_) {
          this.ceremonySeasonCompareError = 'Season comparison unavailable'
        }
      } finally {
        this.finishRequestSource('ceremonySeasonCompare', source)
        if (pollRunId === this.ceremonySeasonComparePollRunId) {
          this.ceremonySeasonCompareLoading = false
        }
      }
    },
    async pollEngagementComparisonJob({ base, headers, jobId, pollRunId }) {
      if (!jobId) return null
      const statusUrl = `${base}/stats/engagement/comparison/jobs/${encodeURIComponent(jobId)}`
      const maxAttempts = 120
      let waitMs = 2000
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (pollRunId !== this.ceremonySeasonComparePollRunId) return null
        const res = await axios.get(statusUrl, { headers })
        const data = res.data || {}
        const status = String(data.status || '').toLowerCase()
        this.ceremonySeasonCompareJobStatus = status || 'queued'
        if (status === 'completed') return data
        if (status === 'failed') return data
        if (attempt >= 14) waitMs = 5000
        await new Promise(resolve => setTimeout(resolve, waitMs))
      }
      return { status: 'failed', message: 'Comparison request timed out. Please try again.' }
    },
    async pollCeremonyBreakdownJob({ base, headers, jobId, pollRunId }) {
      if (!jobId) return null
      const statusUrl = `${base}/stats/ceremony-breakdowns/jobs/${encodeURIComponent(jobId)}`
      const maxAttempts = 120
      let waitMs = 4000
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (pollRunId !== this.ceremonySeasonFactsPollRunId) return null
        const res = await axios.get(statusUrl, { headers })
        const data = res.data || {}
        const status = String(data.status || '').toLowerCase()
        if (status === 'completed') return data
        if (status === 'failed') return data
        if (attempt >= 14) waitMs = 6000
        await new Promise(resolve => setTimeout(resolve, waitMs))
      }
      return { status: 'failed', message: 'Ceremony breakdown request timed out. Please try again.' }
    },
    async loadSeasonCeremonyFacts({ ceremonyIds = null, scopeKey = '' } = {}) {
      if (this.selectedSlug === '_ALL_') return
      let ids = Array.isArray(ceremonyIds) ? ceremonyIds.map(v => String(v)) : (this.ceremonySelectedCeremonyIds || []).map(v => String(v))
      if (!ids.length) ids = (this.surveyCeremonies || []).map(c => String(c.id))
      const key = scopeKey || `${this.selectedSlug}::${[...ids].sort().join(',')}`
      if (this.ceremonySeasonFactsScopeKey === key && !this.ceremonySeasonFactsLoading) return
      const pollRunId = Date.now()
      this.ceremonySeasonFactsPollRunId = pollRunId
      this.ceremonySeasonFactsLoading = true
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payloadBase = {
          universitySlug: this.selectedSlug,
          ceremonyIds: ids,
        }
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('ceremonySeasonFacts')
        const breakdownsRes = await axios.post(url, { ...payloadBase, mode: 'breakdowns' }, { headers, cancelToken: source.token })
        if (pollRunId !== this.ceremonySeasonFactsPollRunId) return
        if (this.ceremonySeasonScopeKey !== key) return
        let breakdownData = breakdownsRes?.data || {}
        if (breakdownsRes?.status === 202 || breakdownData?.job_id) {
          const jobResult = await this.pollCeremonyBreakdownJob({
            base,
            headers,
            jobId: String(breakdownData?.job_id || ''),
            pollRunId,
          })
          if (pollRunId !== this.ceremonySeasonFactsPollRunId) return
          if (this.ceremonySeasonScopeKey !== key) return
          if (!jobResult || !jobResult.result) return
          breakdownData = jobResult.result
        }
        const current = this.ceremonySeasonScopedStats || {}
        this.ceremonySeasonScopedStats = {
          ...current,
          unique_clips_shared: Number(breakdownData?.unique_clips_shared ?? current.unique_clips_shared ?? 0),
          share_platforms: Array.isArray(breakdownData?.share_platforms) ? breakdownData.share_platforms : (current.share_platforms || []),
          timezones_reached: Number(breakdownData?.timezones_reached ?? current.timezones_reached ?? 0),
          top_timezones: Array.isArray(breakdownData?.top_timezones) ? breakdownData.top_timezones : (current.top_timezones || []),
          top_sources: Array.isArray(breakdownData?.top_sources) ? breakdownData.top_sources : (current.top_sources || []),
        }
        this.ceremonySeasonFactsScopeKey = key
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        // Keep ceremony rollup data visible if secondary facts fail.
      } finally {
        this.finishRequestSource('ceremonySeasonFacts', source)
        this.ceremonySeasonFactsLoading = false
      }
    },
    async loadSeasonCeremonyBreakdown({ force = false, loadInsights = false } = {}) {
      if (this.selectedSlug === '_ALL_') return
      let ids = (this.ceremonySelectedCeremonyIds || []).map(v => String(v))
      if (!ids.length) {
        ids = (this.surveyCeremonies || []).map(c => String(c.id))
      }
      const key = `${this.selectedSlug}::${[...ids].sort().join(',')}`
      const needsFacts = this.ceremonyInsightsMode !== 'season_comparison'
      if (!force && this.ceremonySeasonScopeKey === key && this.ceremonySeasonRows.length) {
        if (needsFacts && !this.ceremonySeasonFactsLoading && this.ceremonySeasonFactsScopeKey !== key) {
          this.loadSeasonCeremonyFacts({ ceremonyIds: ids, scopeKey: key })
        }
        if (loadInsights) {
          if (this.ceremonyInsightsMode === 'season_comparison') {
            if (!this.ceremonySeasonCompareData && !this.ceremonySeasonCompareLoading) {
              this.loadSeasonCeremonyComparison({ ceremonyIds: ids, force: false })
            }
          } else if (!this.ceremonySeasonScopedInsights && !this.ceremonySeasonInsightsLoading) {
            this.loadSeasonCeremonyInsights({ ceremonyIds: ids, force: false })
          }
        }
        return
      }
      const inflightFacts = this.requestSources?.ceremonySeasonFacts
      if (inflightFacts && typeof inflightFacts.cancel === 'function') {
        inflightFacts.cancel('Superseded by ceremony rollup refresh')
        this.$set(this.requestSources, 'ceremonySeasonFacts', null)
      }
      const inflightInsights = this.requestSources?.ceremonySeasonInsights
      if (inflightInsights && typeof inflightInsights.cancel === 'function') {
        inflightInsights.cancel('Superseded by ceremony fact refresh')
        this.$set(this.requestSources, 'ceremonySeasonInsights', null)
      }
      const inflightCompare = this.requestSources?.ceremonySeasonCompare
      if (inflightCompare && typeof inflightCompare.cancel === 'function') {
        inflightCompare.cancel('Superseded by ceremony comparison refresh')
        this.$set(this.requestSources, 'ceremonySeasonCompare', null)
        this.ceremonySeasonComparePollRunId = Date.now()
        this.ceremonySeasonCompareJobStatus = ''
      }
      this.ceremonySeasonLoading = true
      let source = null
      try {
        this.ceremonySeasonError = ''
        this.ceremonySeasonInsightsError = ''
        this.ceremonySeasonScopedInsights = null
        this.ceremonySeasonCompareError = ''
        this.ceremonySeasonCompareLoading = false
        this.ceremonySeasonCompareData = null
        this.ceremonySeasonCompareScopeKey = ''
        this.ceremonySeasonCompareJobStatus = ''
        this.ceremonySeasonFactsLoading = false
        this.ceremonySeasonFactsScopeKey = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payload = {
          universitySlug: this.selectedSlug,
          mode: 'ceremony_rollup',
          ceremonyIds: ids,
        }
        if (this.isAdmin) payload.includeBenchmark = true
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('ceremonySeason')
        const rollupRes = await axios.post(url, payload, { headers, cancelToken: source.token })
        const rollupData = rollupRes?.data || {}
        this.ceremonySeasonRows = Array.isArray(rollupData?.by_ceremony) ? rollupData.by_ceremony : []
        const totalUnique = this.ceremonySeasonRows.reduce((sum, row) => sum + Number(row?.unique_views || 0), 0)
        const totalViews = this.ceremonySeasonRows.reduce((sum, row) => sum + Number(row?.total_views || 0), 0)
        const totalShares = this.ceremonySeasonRows.reduce((sum, row) => sum + Number(row?.total_shares || 0), 0)
        const totalDownloads = this.ceremonySeasonRows.reduce((sum, row) => sum + Number(row?.total_downloads || 0), 0)
        this.ceremonySeasonScopedStats = {
          unique_views: Number(rollupData?.unique_views || totalUnique || 0),
          total_views: Number(rollupData?.total_views || totalViews || 0),
          total_shares: Number(rollupData?.total_shares || totalShares || 0),
          total_downloads: Number(rollupData?.total_downloads || totalDownloads || 0),
          graduates: Number(rollupData?.graduates || 0),
          graduate_amplification_index: (rollupData?.graduate_amplification_index ?? null),
          graduate_amplification_index_label: String(rollupData?.graduate_amplification_index_label || ''),
          projected_total_impressions_range: String(rollupData?.projected_total_impressions_range || ''),
          projected_total_impressions_lower_multiplier: Number(rollupData?.projected_total_impressions_lower_multiplier || 0),
          projected_total_impressions_upper_multiplier: Number(rollupData?.projected_total_impressions_upper_multiplier || 0),
          additional_audience_per_graduate: (rollupData?.additional_audience_per_graduate ?? null),
          additional_audience_per_graduate_label: String(rollupData?.additional_audience_per_graduate_label || ''),
          observed_share_amplification: (rollupData?.observed_share_amplification ?? null),
          observed_share_amplification_label: String(rollupData?.observed_share_amplification_label || ''),
          observed_share_amplification_reliability: String(rollupData?.observed_share_amplification_reliability || ''),
          observed_share_amplification_reliability_label: String(rollupData?.observed_share_amplification_reliability_label || ''),
          download_rate: (rollupData?.download_rate ?? null),
          download_rate_label: String(rollupData?.download_rate_label || ''),
          metric_tooltips: rollupData?.metric_tooltips && typeof rollupData.metric_tooltips === 'object' ? rollupData.metric_tooltips : {},
          benchmark_yearly: rollupData?.benchmark_yearly && typeof rollupData.benchmark_yearly === 'object' ? rollupData.benchmark_yearly : null,
          sessions: 0,
          page_views: 0,
          unique_clips_shared: 0,
          share_platforms: [],
          timezones_reached: 0,
          top_timezones: [],
          top_sources: [],
        }
        this.ceremonySeasonScopeKey = key
        if (this.ceremonyInsightsMode !== 'season_comparison') {
          this.prefetchCeremonyNames()
        }
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.ceremonySeasonRows = []
        this.ceremonySeasonScopedStats = null
        this.ceremonySeasonScopedInsights = null
        try { this.ceremonySeasonError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.ceremonySeasonError = 'Unexpected error' }
      } finally {
        this.finishRequestSource('ceremonySeason', source)
        this.ceremonySeasonLoading = false
      }
      if (!this.ceremonySeasonError && needsFacts) {
        this.loadSeasonCeremonyFacts({ ceremonyIds: ids, scopeKey: key })
      }
      if (loadInsights && !this.ceremonySeasonError) {
        if (this.ceremonyInsightsMode === 'season_comparison') {
          this.loadSeasonCeremonyComparison({ ceremonyIds: ids, force })
        } else {
          this.loadSeasonCeremonyInsights({ ceremonyIds: ids, force })
        }
      }
    },
    async loadWindowCeremonyBreakdown({ force = false } = {}) {
      if (this.selectedSlug === '_ALL_') return
      const key = `${this.selectedSlug}::${this.start || ''}::${this.end || ''}`
      if (!force && this.ceremonyWindowScopeKey === key && this.ceremonyWindowRows.length) return
      this.ceremonyWindowLoading = true
      let source = null
      try {
        this.ceremonyWindowError = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payload = {
          universitySlug: this.selectedSlug,
          mode: 'ceremony_rollup',
        }
        if (this.start) payload.start = this.start
        if (this.end) payload.end = this.end
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('ceremonyWindow')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.ceremonyWindowRows = Array.isArray(r?.data?.by_ceremony) ? r.data.by_ceremony : []
        this.ceremonyWindowScopeKey = key
        this.prefetchCeremonyNames()
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.ceremonyWindowRows = []
        try { this.ceremonyWindowError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.ceremonyWindowError = 'Unexpected error' }
      } finally {
        this.finishRequestSource('ceremonyWindow', source)
        this.ceremonyWindowLoading = false
      }
    },
    engagementUniversityScopePayload() {
      if (this.isAllScope) {
        if (!this.canLoadAllScopeEngagement) return null
        const slugs = this.engagementScopeSlugs
        if (!slugs.length) return null
        return { universitySlugs: slugs }
      }
      if (!this.selectedSlug) return null
      return { universitySlug: this.selectedSlug }
    },
    async loadBenchmarkOverview() {
      if (!this.isAdmin) {
        this.benchmarkOverview = null
        this.benchmarkOverviewError = ''
        this.benchmarkOverviewLoading = false
        return
      }
      const slugs = (this.allowedUniversities || [])
        .map(u => String(u?.slug || '').trim())
        .filter(Boolean)
      if (!slugs.length) {
        this.benchmarkOverview = null
        this.benchmarkOverviewError = 'No universities in admin scope.'
        this.benchmarkOverviewLoading = false
        return
      }
      this.benchmarkOverviewLoading = true
      this.benchmarkOverviewError = ''
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university/benchmarks'
        const payload = {
          mode: 'benchmark_overview',
          universitySlugs: slugs,
        }
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('benchmarkOverview')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.benchmarkOverview = r?.data?.benchmark_overview || null
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.benchmarkOverview = null
        try {
          this.benchmarkOverviewError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Benchmark overview unavailable'
        } catch (_) {
          this.benchmarkOverviewError = 'Benchmark overview unavailable'
        }
      } finally {
        this.finishRequestSource('benchmarkOverview', source)
        this.benchmarkOverviewLoading = false
      }
    },
    async loadEngagementBreakdowns() {
      if (this.selectedSlug === '_ALL_' && !this.canLoadAllScopeEngagement) return
      const scopePayload = this.engagementUniversityScopePayload()
      if (!scopePayload) return
      this.breakdownsLoading = true
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payload = {
          mode: 'breakdowns',
          ...scopePayload,
        }
        if (this.start) payload.start = this.start
        if (this.end) payload.end = this.end
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('breakdowns')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.stats = {
          ...(this.stats || {}),
          unique_clips_shared: r?.data?.unique_clips_shared || 0,
          share_platforms: Array.isArray(r?.data?.share_platforms) ? r.data.share_platforms : [],
          timezones_reached: r?.data?.timezones_reached || 0,
          top_timezones: Array.isArray(r?.data?.top_timezones) ? r.data.top_timezones : [],
          top_sources: Array.isArray(r?.data?.top_sources) ? r.data.top_sources : [],
        }
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        // Keep summary visible even if secondary breakdowns fail.
      } finally {
        this.finishRequestSource('breakdowns', source)
        this.breakdownsLoading = false
      }
    },
    async loadEngagementInsights() {
      if (this.selectedSlug === '_ALL_' && !this.canLoadAllScopeEngagement) return
      const scopePayload = this.engagementUniversityScopePayload()
      if (!scopePayload) return
      this.engagementInsightsLoading = true
      this.engagementInsightsError = ''
      this.engagementDeterministicInsightsCollapsed = true
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/stats/university'
        const payload = {
          mode: 'engagement_insights',
          includeNarrative: true,
          ...scopePayload,
        }
        if (this.start) payload.start = this.start
        if (this.end) payload.end = this.end
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('engagementInsights')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.engagementInsights = r?.data?.engagement_insights || null
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        this.engagementInsights = null
        try {
          this.engagementInsightsError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Insights unavailable'
        } catch (_) {
          this.engagementInsightsError = 'Insights unavailable'
        }
      } finally {
        this.finishRequestSource('engagementInsights', source)
        this.engagementInsightsLoading = false
      }
    },
    clearEngagementState() {
      this.cancelStatsRequests()
      this.cancelTopClipRequests()
      this.stats = null
      this.engagementInsights = null
      this.engagementInsightsLoading = false
      this.engagementInsightsError = ''
      this.engagementDeterministicInsightsCollapsed = true
      this.benchmarkOverview = null
      this.benchmarkOverviewLoading = false
      this.benchmarkOverviewError = ''
      this.errorMsg = ''
      this.loading = false
      this.expandedCeremonies = {}
      this.ceremonySeasonRows = []
      this.ceremonySeasonLoading = false
      this.ceremonySeasonError = ''
      this.ceremonySeasonScopeKey = ''
      this.ceremonyInsightsMode = 'season_insights'
      this.ceremonyCompareSeasonKey = ''
      this.ceremonySeasonScopedStats = null
      this.ceremonySeasonScopedInsights = null
      this.ceremonySeasonInsightsLoading = false
      this.ceremonySeasonInsightsError = ''
      this.ceremonySeasonFactsLoading = false
      this.ceremonySeasonFactsScopeKey = ''
      this.ceremonySeasonFactsPollRunId = 0
      this.ceremonySeasonCompareScopeKey = ''
      this.ceremonySeasonCompareData = null
      this.ceremonySeasonCompareLoading = false
      this.ceremonySeasonCompareError = ''
      this.ceremonySeasonComparePollRunId = 0
      this.ceremonySeasonCompareJobStatus = ''
      this.ceremonyDeterministicInsightsCollapsed = true
      this.ceremonyWindowRows = []
      this.ceremonyWindowLoading = false
      this.ceremonyWindowError = ''
      this.ceremonyWindowScopeKey = ''
      this.ceremonyTopClipsLoading = {}
      this.topClipRequestSources = {}
      this.breakdownsLoading = false
    },
    clearLiveState() {
      this.liveStats = null
      this.liveLoading = false
      this.liveError = ''
    },
    clearSurveyState() {
      this.surveyStats = null
      this.surveyError = ''
      this.surveyLoading = false
      this.surveyCeremonies = []
      this.surveySeasonKey = 'all'
      this.surveySelectedCeremonyIds = []
      this.surveyCeremonySelectionDraft = []
      this.surveyCeremonyDropdownOpen = false
      this.surveyAnalysisTab = 'overview'
      this.surveySelectedQuestionKey = ''
      this.surveyQuestionCeremonyRows = []
      this.surveyQuestionCeremonyLoading = false
      this.surveyQuestionCeremonyError = ''
      this.surveyQuestionCeremonyViewMode = 'table'
      this.surveyQuestionCeremonySortMode = 'date_asc'
      this.surveyQuestionCeremonyChartMetric = 'answer_rate_pct'
      this.surveyComparisonStats = null
      this.surveyComparisonLoading = false
      this.surveyComparisonError = ''
      this.surveyQuestionCompareLoading = false
      this.surveyQuestionCompareError = ''
      this.surveyComparisonRowsCollapsed = true
      this.surveyQuestionComparisonRowsCollapsed = true
      this.surveyComparisonChartLimit = 15
      this.surveyComparisonChartOrientation = 'horizontal'
      this.surveyQuestionComparisonChartLimit = 15
      this.surveyQuestionComparisonChartOrientation = 'horizontal'
      this.surveyCompareMode = 'ceremony'
      this.surveyCompareMetric = 'response_rate_pct'
      this.surveyCompareSortMode = 'metric_desc'
      this.surveyCompareQuestionKey = ''
      this.surveyQuestionCompareMetric = 'answer_rate_pct'
      this.surveyQuestionCompareSortMode = 'metric_desc'
      this.surveyOptionChartLimit = 20
      this.surveyOptionChartOrientation = 'horizontal'
      this.surveyInsights = null
      this.surveyInsightsLoading = false
      this.surveyInsightsNarrativeLoading = false
      this.surveyInsightsError = ''
      this.surveyInsightsCaveatsCollapsed = true
      this.surveyInsightsSegmentBy = 'ceremony'
      this.surveyInsightsPollRunId = 0
      this.surveyInsightsGeneratedAt = ''
      this.surveyInsightsLoadedFromCache = false
      this.surveyInsightCardExpanded = {}
      this.surveyDetailedInsightExpanded = {}
      this.surveyExportMode = 'full_dataset'
      this.surveyExportFieldPreset = 'full_response'
      this.surveyExportQuestionKey = ''
      this.surveyExportAnswerValues = []
      this.surveyExportPermissionQuestionKey = ''
      this.surveyExportPermissionAnswerValues = []
      this.surveyExportContactableOnly = true
      this.surveyExportLoading = false
      this.surveyExportPreviewLoading = false
      this.surveyExportError = ''
      this.surveyExportPreview = null
      this.surveyReportPack = 'executive'
      this.surveyReportLoading = false
      this.surveyReportError = ''
    },
    async onUniversityChange() {
      if (
        this.activeAnalyticsTab === 'benchmarks' && this.selectedSlug !== '_ALL_'
        || ((this.activeAnalyticsTab === 'ceremony' || this.activeAnalyticsTab === 'surveys' || this.activeAnalyticsTab === 'email') && this.selectedSlug === '_ALL_')
      ) {
        this.activeAnalyticsTab = 'engagement'
      }
      if (this.selectedSlug === '_ALL_') {
        this.clearEngagementState()
        this.clearLiveState()
        this.clearSurveyState()
        if (this.canLoadAllScopeEngagement && this.activeAnalyticsTab === 'engagement') {
          this.loadEngagement()
        }
        if (this.activeAnalyticsTab === 'benchmarks' && this.isAdmin) {
          this.loadBenchmarkOverview()
        }
        if (this.activeAnalyticsTab === 'engagement') this.loadLive()
        return
      }
      this.clearEngagementState()
      this.clearLiveState()
      this.clearSurveyState()
      this.emailStats = null
      this.emailError = ''
      const refreshSurveyCeremoniesPromise = this.refreshSurveyCeremonies()
        .catch(() => {
          this.surveyError = 'Failed to load ceremonies for survey scoping.'
        })
      this.loadEngagement()
      if (this.activeAnalyticsTab === 'engagement') this.loadLive()
      this.ceremonyDataScope = 'season'
      if (this.activeAnalyticsTab === 'ceremony') {
        this.loadSeasonCeremonyBreakdown({ force: true, loadInsights: true })
      }
      if (this.activeAnalyticsTab === 'benchmarks' && this.isAdmin) {
        this.loadBenchmarkOverview()
      }
      try {
        await refreshSurveyCeremoniesPromise
      } catch (_) { void 0 }
      if (this.activeAnalyticsTab === 'surveys') this.loadSurvey()
      if (this.activeAnalyticsTab === 'email') this.loadEmailEngagement()
    },
    async fetchUniversities() {
      // Fetch full list to map slugs -> names; filter to allowed slugs from JWT
      try {
        const r = await axios.get('/universities')
        const list = Array.isArray(r.data) ? r.data : []
        const allowed = new Set(this.universities)
        this.allowedUniversities = list.filter(u => allowed.has(u.slug))
        if (!this.selectedSlug && this.allowedUniversities.length) {
          this.selectedSlug = this.allowedUniversities[0].slug
        }
      } catch (e) {
        // Fallback: just use the slug from token
        if (!this.selectedSlug && this.universities && this.universities.length) {
          this.selectedSlug = this.universities[0]
        }
        this.allowedUniversities = (this.universities || []).map(s => ({ slug: s, name: s }))
      }
    },
    applyPreset(key) {
      this.activePreset = key
      this.emailAllTime = false
      this.toggleCustom = false
      const p = this.presets.find(x => x.key === key)
      if (!p) return
      const now = Date.now()
      this.start = now - p.deltaMs
      this.end = now
      if (this.activeAnalyticsTab === 'engagement' && (!this.isAllScope || this.canLoadAllScopeEngagement)) {
        this.loadEngagement()
      } else if (this.activeAnalyticsTab === 'email' && !this.isAllScope) {
        this.loadEmailEngagement()
      } else if (this.isAllScope) {
        this.clearEngagementState()
      } else {
        this.stats = null
        this.engagementInsights = null
        this.errorMsg = ''
      }
    },
    applyCustom() {
      if (this.customFrom) this.start = new Date(this.customFrom).getTime()
      if (this.customTo) this.end = new Date(this.customTo).getTime()
      this.activePreset = ''
      if (this.activeAnalyticsTab === 'engagement' && (!this.isAllScope || this.canLoadAllScopeEngagement)) {
        this.loadEngagement()
      } else if (this.isAllScope) {
        this.clearEngagementState()
      } else {
        this.stats = null
        this.engagementInsights = null
        this.errorMsg = ''
      }
    },
    async loadEngagement() {
      if (this.selectedSlug === '_ALL_' && !this.canLoadAllScopeEngagement) {
        this.clearEngagementState()
        return
      }
      const scopePayload = this.engagementUniversityScopePayload()
      if (!scopePayload) {
        this.clearEngagementState()
        return
      }
      this.loading = true
      let source = null
      try {
        this.errorMsg = ''
        this.stats = null
        this.engagementInsights = null
        this.engagementInsightsError = ''
        this.benchmarkOverviewError = ''
        if (!this.canLoadAllScopeEngagement) this.benchmarkOverview = null
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const body = {}
        if (this.start) body.start = this.start
        if (this.end) body.end = this.end
        const url = base + '/stats/university'
        const payload = { ...body, mode: 'summary', ...scopePayload }
        if (this.isAdmin) payload.includeBenchmark = true
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        source = this.startRequestSource('summary')
        const r = await axios.post(url, payload, { headers, cancelToken: source.token })
        this.stats = {
          ...r.data,
          unique_clips_shared: 0,
          share_platforms: [],
          timezones_reached: 0,
          top_timezones: [],
          top_sources: [],
        }
        this.expandedCeremonies = {}
        this.loadEngagementBreakdowns()
        this.loadEngagementInsights()
      } catch (e) {
        if (this.isRequestCancelled(e)) return
        console.error(e)
        this.stats = null
        try { this.errorMsg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.errorMsg = 'Unexpected error' }
      } finally {
        this.finishRequestSource('summary', source)
        this.loading = false
      }
    },
    async loadEmailEngagement() {
      if (!this.selectedSlug || this.isAllScope) {
        this.emailStats = null
        return
      }
      this.emailLoading = true
      this.emailError = ''
      let source = null
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const payload = { universitySlug: this.selectedSlug }
        if (!this.emailAllTime && this.start) payload.start = this.start
        if (!this.emailAllTime && this.end) payload.end = this.end
        source = this.startRequestSource('email')
        const response = await axios.post(base + '/stats/email-engagement', payload, { headers, cancelToken: source.token })
        this.emailStats = response.data
      } catch (error) {
        if (this.isRequestCancelled(error)) return
        this.emailStats = null
        try { this.emailError = error?.response?.data?.message || error?.message || 'Unexpected error' } catch (_) { this.emailError = 'Unexpected error' }
      } finally {
        const isCurrentRequest = this.requestSources?.email === source
        this.finishRequestSource('email', source)
        if (isCurrentRequest) this.emailLoading = false
      }
    },
    loadAllEmailEngagement() {
      this.emailAllTime = true
      this.loadEmailEngagement()
    },
    async loadSurvey() {
      if (this.selectedSlug === '_ALL_') {
        this.clearSurveyState()
        return
      }
      this.surveyLoading = true
      try {
        this.surveyError = ''
        this.surveyStats = null
        this.surveyQuestionCeremonyError = ''
        this.surveyQuestionCeremonyRows = []
        this.surveyInsights = null
        this.surveyInsightsLoading = false
        this.surveyInsightsNarrativeLoading = false
        this.surveyInsightsError = ''
        this.surveyInsightsPollRunId = 0
        this.surveyInsightsGeneratedAt = ''
        this.surveyInsightsLoadedFromCache = false
        this.surveyInsightCardExpanded = {}
        this.surveyDetailedInsightExpanded = {}
        this.surveyExportError = ''
        this.surveyExportPreview = null
        this.surveyReportError = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const surveyUrl = base + '/stats/survey'
        const selectedCeremonyIds = (this.surveySelectedCeremonyIds || []).map(v => String(v))
        this.surveyCeremonySelectionDraft = [...selectedCeremonyIds]
        const requestedQuestionKey = this.surveySelectedQuestionKey || ''
        const payload = {
          universitySlug: this.selectedSlug,
          ceremonyIds: selectedCeremonyIds,
        }
        if (requestedQuestionKey) payload.questionKeys = [requestedQuestionKey]
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const r = await axios.post(surveyUrl, payload, { headers })
        this.surveyStats = r.data
        const questions = Array.isArray(r?.data?.questions) ? r.data.questions : []
        if (!questions.find(q => q?.question_key === this.surveySelectedQuestionKey)) {
          this.surveySelectedQuestionKey = questions[0]?.question_key || ''
        }
        this.ensureSurveyExportSelection(questions)
        this.ensureSurveyCompareQuestionSelection(questions)
        const activeQuestionKey = this.surveySelectedQuestionKey || ''
        const scopedQuestionRows = Array.isArray(r?.data?.question_ceremony) ? r.data.question_ceremony : []
        if (activeQuestionKey && requestedQuestionKey && requestedQuestionKey === activeQuestionKey) {
          this.surveyQuestionCeremonyRows = scopedQuestionRows
        } else if (activeQuestionKey) {
          await this.loadSurveyQuestionCeremony({
            surveyUrl,
            headers,
            selectedCeremonyIds,
            questionKey: activeQuestionKey,
          })
        }

        const allCeremonyIds = (this.surveyCeremonies || []).map(c => String(c.id))
        const comparisonCeremonyIds = this.comparisonCeremonyIdsForMode(
          this.surveyCompareMode,
          selectedCeremonyIds,
          allCeremonyIds
        )
        const compareQuestionKey = String(this.surveyCompareQuestionKey || activeQuestionKey || '')
        if (this.sameIdSet(selectedCeremonyIds, comparisonCeremonyIds)) {
          this.surveyComparisonStats = r.data
          this.surveyComparisonError = ''
          this.surveyComparisonLoading = false
          this.ensureSurveyCompareQuestionSelection(Array.isArray(r?.data?.questions) ? r.data.questions : null)
          if (compareQuestionKey && requestedQuestionKey !== compareQuestionKey) {
            this.loadSurveyComparison({ surveyUrl, headers, ceremonyIds: comparisonCeremonyIds, questionKey: compareQuestionKey })
          }
        } else {
          this.loadSurveyComparison({ surveyUrl, headers, ceremonyIds: comparisonCeremonyIds, questionKey: compareQuestionKey })
        }
      } catch (e) {
        this.surveyStats = null
        this.surveyQuestionCeremonyRows = []
        try { this.surveyError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.surveyError = 'Unexpected error' }
      } finally {
        this.surveyLoading = false
      }
    },
    async loadSurveyQuestionCeremony({ surveyUrl = null, headers = null, selectedCeremonyIds = null, questionKey = null } = {}) {
      if (this.selectedSlug === '_ALL_') return
      const resolvedQuestionKey = String(questionKey || this.surveySelectedQuestionKey || '')
      if (!resolvedQuestionKey) {
        this.surveyQuestionCeremonyRows = []
        this.surveyQuestionCeremonyError = ''
        return
      }
      this.surveyQuestionCeremonyLoading = true
      try {
        this.surveyQuestionCeremonyError = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const resolvedSurveyUrl = surveyUrl || (base + '/stats/survey')
        const resolvedHeaders = headers || (() => {
          const token = localStorage.getItem('token') || ''
          return token ? { Authorization: `Bearer ${token}` } : {}
        })()
        const resolvedIds = Array.isArray(selectedCeremonyIds)
          ? selectedCeremonyIds.map(v => String(v))
          : (this.surveySelectedCeremonyIds || []).map(v => String(v))
        const payload = {
          universitySlug: this.selectedSlug,
          ceremonyIds: resolvedIds,
          questionKeys: [resolvedQuestionKey],
        }
        const r = await axios.post(resolvedSurveyUrl, payload, { headers: resolvedHeaders })
        const rows = Array.isArray(r?.data?.question_ceremony) ? r.data.question_ceremony : []
        if (String(this.surveySelectedQuestionKey || '') === resolvedQuestionKey) {
          this.surveyQuestionCeremonyRows = rows
        }
      } catch (e) {
        this.surveyQuestionCeremonyRows = []
        try { this.surveyQuestionCeremonyError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.surveyQuestionCeremonyError = 'Unexpected error' }
      } finally {
        this.surveyQuestionCeremonyLoading = false
      }
    },
    async loadSurveyComparison({
      surveyUrl = null,
      headers = null,
      ceremonyIds = null,
      questionKey = null,
      loadingScope = 'global',
    } = {}) {
      if (this.selectedSlug === '_ALL_') return
      const isQuestionScope = loadingScope === 'question'
      if (isQuestionScope) {
        this.surveyQuestionCompareLoading = true
        this.surveyQuestionCompareError = ''
      } else {
        this.surveyComparisonLoading = true
        this.surveyComparisonError = ''
      }
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const resolvedSurveyUrl = surveyUrl || (base + '/stats/survey')
        const resolvedHeaders = headers || (() => {
          const token = localStorage.getItem('token') || ''
          return token ? { Authorization: `Bearer ${token}` } : {}
        })()
        const resolvedIds = Array.isArray(ceremonyIds)
          ? ceremonyIds.map(v => String(v))
          : this.comparisonCeremonyIdsForMode(this.surveyCompareMode)
        const payload = {
          universitySlug: this.selectedSlug,
          ceremonyIds: resolvedIds,
        }
        const resolvedQuestionKey = String(questionKey || this.surveyCompareQuestionKey || this.surveySelectedQuestionKey || '')
        if (resolvedQuestionKey) payload.questionKeys = [resolvedQuestionKey]
        const r = await axios.post(resolvedSurveyUrl, payload, { headers: resolvedHeaders })
        this.surveyComparisonStats = r.data
        this.ensureSurveyCompareQuestionSelection(Array.isArray(r?.data?.questions) ? r.data.questions : null)
      } catch (e) {
        if (!isQuestionScope) {
          this.surveyComparisonStats = null
        }
        try {
          const message = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error'
          if (isQuestionScope) {
            this.surveyQuestionCompareError = message
          } else {
            this.surveyComparisonError = message
          }
        } catch {
          if (isQuestionScope) {
            this.surveyQuestionCompareError = 'Unexpected error'
          } else {
            this.surveyComparisonError = 'Unexpected error'
          }
        }
      } finally {
        if (isQuestionScope) {
          this.surveyQuestionCompareLoading = false
        } else {
          this.surveyComparisonLoading = false
        }
      }
    },
    async loadLive() {
      this.liveLoading = this.liveStats ? false : true
      try {
        this.liveError = ''
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + '/live/summary'
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const payload = { windowSeconds: this.activeLiveWindow }
        if (this.selectedSlug && this.selectedSlug !== '_ALL_') payload.universitySlug = this.selectedSlug
        const r = await axios.post(url, payload, { headers })
        this.liveStats = r.data
      } catch (e) {
        try { this.liveError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Unexpected error' } catch { this.liveError = 'Unexpected error' }
      } finally {
        this.liveLoading = false
      }
    },
    startLiveAutoRefresh() {
      if (this.activeAnalyticsTab !== 'engagement') return
      if (this.liveTimer) clearInterval(this.liveTimer)
      this.loadLive()
      this.liveTimer = setInterval(this.loadLive, 5000)
    },
    stopLiveAutoRefresh() {
      if (this.liveTimer) clearInterval(this.liveTimer)
      this.liveTimer = null
    },
    formatDateLabel(ts) {
      if (!ts) return ''
      const formatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      return formatter.format(new Date(ts))
    },
    async exportReport() {
      if (this.isAllScope) {
        this.exportError = 'Select a university to export a report.'
        return
      }
      this.exporting = true
      this.exportError = ''
      try {
        const base = process.env.VUE_APP_ANALYTICS_API_BASE_URL || ''
        const url = base + 'reports/university'
        const token = localStorage.getItem('token') || ''
        const headers = token ? { Authorization: `Bearer ${token}` } : {}
        const fromLabel = this.formatDateLabel(this.start)
        const toLabel = this.formatDateLabel(this.end)
        const isCeremonyReport = this.activeAnalyticsTab === 'ceremony'
        const ceremonyIds = isCeremonyReport
          ? (this.ceremonySelectedCeremonyIds || []).map(v => String(v)).filter(Boolean)
          : []
        const seasonLabel = isCeremonyReport ? this.ceremonySeasonScopeLabel : ''
        const rangeLabel = isCeremonyReport
          ? seasonLabel
          : (fromLabel && toLabel ? `${fromLabel} - ${toLabel}` : '')
        const payload = {
          universitySlug: this.selectedSlug,
          universityName: this.selectedUniversityName,
          reportTitle: isCeremonyReport ? 'MyGraduationClip Season Engagement Report' : 'MyGraduationClip Usage Report',
          dateRangeLabel: rangeLabel,
          reportDateLabel: this.formatDateLabel(Date.now()),
        }
        if (isCeremonyReport) {
          payload.ceremonyIds = ceremonyIds
          payload.currentSeasonLabel = seasonLabel
          payload.scopeLabel = seasonLabel
        } else {
          if (this.start) payload.start = this.start
          if (this.end) payload.end = this.end
        }
        const r = await axios.post(url, payload, { headers })
        const data = r.data || {}
        if (!data.pdf_base64) {
          throw new Error('Missing PDF data')
        }
        const binary = atob(data.pdf_base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes], { type: data.content_type || 'application/pdf' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = data.filename || `${this.selectedSlug}-analytics-report.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(link.href), 1000)
        trackAdminEvent('admin_action_completed', {
          toolKey: 'engagement_analytics',
          metadata: {
            action: 'download_analytics_report',
            universitySlug: this.selectedSlug,
            tab: this.activeAnalyticsTab,
          },
        })
      } catch (e) {
        try { this.exportError = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || 'Export failed' } catch { this.exportError = 'Export failed' }
      } finally {
        this.exporting = false
      }
    }
  },
  async mounted() {
    await this.fetchUniversities()
    this.applyPreset(this.activePreset)
    if (!this.isAllScope) {
      try {
        await this.refreshSurveyCeremonies()
        await this.loadSurvey()
      } catch (_) {
        this.surveyError = 'Failed to initialize survey analytics.'
      }
    }
    this._onTooltipViewportChange = () => {
      if (this.activeInfoTipEl && this.floatingTooltip.visible) {
        this.positionFloatingTooltip(this.activeInfoTipEl)
      }
    }
    this._onDocumentClick = (event) => {
      this.handleDocumentClick(event)
    }
    window.addEventListener('resize', this._onTooltipViewportChange)
    window.addEventListener('scroll', this._onTooltipViewportChange, true)
    document.addEventListener('click', this._onDocumentClick, true)
    if (this.activeAnalyticsTab === 'engagement') {
      this.startLiveAutoRefresh()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onTooltipViewportChange)
    window.removeEventListener('scroll', this._onTooltipViewportChange, true)
    document.removeEventListener('click', this._onDocumentClick, true)
    this.hideFloatingTooltip()
    this.cancelStatsRequests()
    this.cancelTopClipRequests()
    this.stopLiveAutoRefresh()
  }
}
</script>

<style scoped>
.container { max-width: 1040px; margin: 0 auto; padding: 8px 12px; }
.info-tip { position: relative; display: inline-block; line-height: 1; }
.info-tooltip-floating {
  position: fixed;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.96);
  color: #fff;
  padding: 8px 10px;
  border-radius: 6px;
  max-width: min(420px, 80vw);
  font-size: 12px;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  z-index: 9999;
  pointer-events: none;
}
.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(17, 24, 39, 0.2);
  border-top-color: rgba(17, 24, 39, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
